/**
 * Persistent session manager for Claude Code agents.
 *
 * - **tmux** for the interactive session (channels like Slack/Telegram
 *   require a real TTY that only tmux provides). Task injection lands in
 *   the same session via tmux send-keys.
 *
 * On manager restart, detects existing tmux sessions and reattaches
 * without creating duplicates.
 */

import { spawn, execSync, execFileSync, type ChildProcess } from 'node:child_process';
import { join, dirname } from 'node:path';
import { homedir, platform, userInfo } from 'node:os';
import { existsSync, readFileSync, readdirSync, writeFileSync, appendFileSync, mkdirSync, chmodSync, copyFileSync, rmSync } from 'node:fs';
import { sanitizeMcpJson } from './mcp-sanitize.js';
import { buildAllowedTools } from './claude-tools.js';
import { probeMcpEnvSubstitution, formatMissingVar } from './mcp-env-probe.js';
import { claudeModelAlias, isClaudeFastMode } from './claude-model-alias.js';
import { reapOrphanChannelMcps } from './orphan-channel-mcp-reaper.js';
import { randomUUID } from 'node:crypto';
import {
  getOrCreateDailySession,
  markDailySessionSpawn,
  rotateDailySession,
  sessionFileExists,
} from './daily-session.js';
// ENG-5832: the tmux/pgrep zombie-probe primitives now live in
// @augmented/core so the channel servers (packages/mcp) can share the exact
// same pgrep matching when deciding whether an inbound can be answered. The
// stateful bookkeeping + dead-session teardown below stays CLI-only.
import { probeClaudeProcessInTmux } from '@augmented/core/runtime/session-probe.js';
// ENG-6017: shared dialog detection/dismissal (also consumed by the
// channel-input-watchdog) plus input-box extraction for the inject-time
// pane hygiene below.
import {
  isLoginPickerVisible,
  isResumeModeDialogVisible,
  sweepDialogs,
  sendDialogKeys,
  simpleTextHash,
} from './claude-dialogs.js';
import { extractInputBoxText } from './channel-input-watchdog.js';

/**
 * ENG-7152: OpenRouter's native Anthropic-Messages endpoint. Claude Code speaks
 * its own protocol directly against this base URL (no translation proxy) when
 * an agent is in OpenRouter BYO-model mode — `/v1/messages` is appended by the
 * client. Validated live against open-source models in spike ENG-7148.
 */
const OPENROUTER_ANTHROPIC_BASE_URL = 'https://openrouter.ai/api';

/**
 * When running as root on Linux, the tmux-spawned claude process reads
 * ~/.claude/.credentials.json from /root. But operators log in via `claude
 * /login` as ssm-user or ec2-user, leaving creds under their own home.
 * Copy the first valid creds file into /root/.claude so claude (running as
 * root inside tmux) finds them. Idempotent — safe to call on every spawn.
 *
 * Returns true if a copy was made (or the file is already up to date),
 * false if no creds could be found at all.
 */
function syncClaudeCredsToRoot(): boolean {
  if (platform() !== 'linux') return true;
  if (typeof process.getuid !== 'function' || process.getuid() !== 0) return true;

  // Fast path: pair-via-browser writes creds directly to /root/.claude
  // (the throwaway claude session runs as root). If they're already
  // there, no sync needed.
  for (const filename of ['.credentials.json', 'credentials.json']) {
    if (existsSync(join('/root/.claude', filename))) return true;
  }

  // Legacy path: an operator ran `claude /login` interactively as
  // ec2-user. Find any /home/*/.claude credentials and copy them up.
  let sourcePath: string | null = null;
  try {
    const entries = readdirSync('/home', { withFileTypes: true });
    outer: for (const entry of entries) {
      if (!entry.isDirectory()) continue;
      // Both filenames Claude Code has historically used — keep in sync
      // with findClaudeCredentialsPaths() in claude-auth-detect.ts.
      for (const filename of ['.credentials.json', 'credentials.json']) {
        const candidate = join('/home', entry.name, '.claude', filename);
        if (existsSync(candidate)) {
          sourcePath = candidate;
          break outer;
        }
      }
    }
  } catch { /* no /home or unreadable — fall through */ }

  if (!sourcePath) return false;

  const targetDir = '/root/.claude';
  // Preserve source filename so the resulting file matches what claude's
  // reader expects (it accepts either '.credentials.json' or 'credentials.json').
  const sourceFilename = sourcePath.endsWith('credentials.json') && !sourcePath.endsWith('.credentials.json')
    ? 'credentials.json'
    : '.credentials.json';
  const targetPath = join(targetDir, sourceFilename);
  try {
    if (!existsSync(targetDir)) mkdirSync(targetDir, { recursive: true, mode: 0o700 });
    copyFileSync(sourcePath, targetPath);
    chmodSync(targetPath, 0o600);
    return true;
  } catch {
    return false;
  }
}

/**
 * ENG-7213: decide what to do with the host's claude.ai OAuth creds for a given
 * auth configuration. The discriminator is the INFERENCE source; channel OAuth
 * is orthogonal:
 *   - 'sync'  keep/sync the OAuth creds under /root/.claude. subscription mode
 *             uses them for inference; openrouter mode uses them ONLY to unlock
 *             the claude.ai-gated channels feature (inference goes to OpenRouter
 *             via the ANTHROPIC_BASE_URL/ANTHROPIC_AUTH_TOKEN override).
 *   - 'purge' remove them - genuine api_key mode ONLY, so a stale OAuth session
 *             can't shadow ANTHROPIC_API_KEY (precedence is version-dependent and
 *             undocumented). OpenRouter is exempt: it uses ANTHROPIC_AUTH_TOKEN +
 *             a base-URL override, not ANTHROPIC_API_KEY, so there is no
 *             api-key/OAuth ambiguity to resolve.
 *
 * The decision keys off BOTH the runtime openRouterMode flag AND the stored
 * claudeAuthMode: a host saved as 'openrouter' whose agent has not yet been
 * provisioned an OpenRouter key would (in principle) spawn with
 * openRouterMode=false, and must still KEEP its paired OAuth for channels rather
 * than purge it. The live manager already refuses to spawn such an agent (the
 * openrouter no-key skip in manager-worker.ts), so reaching here with
 * ('openrouter', false) is defensive - this keeps the helper's contract honest.
 */
export function resolveOAuthCredAction(
  claudeAuthMode: 'subscription' | 'api_key' | 'openrouter',
  openRouterMode: boolean,
): 'sync' | 'purge' {
  if (openRouterMode) return 'sync';
  // Only genuine api_key inference purges; subscription and (stored) openrouter
  // both retain OAuth.
  return claudeAuthMode === 'api_key' ? 'purge' : 'sync';
}

/**
 * Resolve the claude binary to an absolute path. The manager runs under a
 * minimal PATH (cloud-init root env) that doesn't include
 * /home/linuxbrew/.linuxbrew/bin, so a bare `claude` reference in the tmux
 * shell fails immediately — session exits, manager sees it as "unhealthy",
 * restarts, loops forever.
 *
 * Cached at first call: claude's location doesn't change between cycles,
 * and `which` spawns aren't free.
 */
let cachedClaudePath: string | null = null;
export function resolveClaudeBinary(): string {
  if (cachedClaudePath) return cachedClaudePath;
  // Operator override: honour CLAUDE_PATH for non-standard installs.
  const override = process.env.CLAUDE_PATH;
  if (override && existsSync(override)) {
    cachedClaudePath = override;
    return override;
  }
  // Try PATH first — respects an operator's custom install.
  try {
    const out = execSync('which claude 2>/dev/null', { encoding: 'utf-8' }).trim();
    if (out && existsSync(out)) {
      cachedClaudePath = out;
      return out;
    }
  } catch { /* fall through to canonical paths */ }
  const candidates = [
    '/home/linuxbrew/.linuxbrew/bin/claude',
    '/opt/homebrew/bin/claude',
    '/usr/local/bin/claude',
  ];
  for (const p of candidates) {
    if (existsSync(p)) {
      cachedClaudePath = p;
      return p;
    }
  }
  // Last resort — let the shell fail so logs show the missing binary.
  return 'claude';
}

/**
 * Per-agent isolation mode (ADR-0014, Docker path). Gated by the
 * `AGT_ISOLATION` env var so it's reversible per-host with no code change -
 * the spike step stands up exactly one host. Forward-compatible with the
 * ADR-0022 feature-flag system: when that grows consumers, this becomes the
 * `envVar` escape hatch of an `agent-isolation-mode` enum flag (none|docker),
 * and per-agent risk-tier gating drops in without touching this call site.
 *
 * ponytail: env-var gate, not the DB flag system - that system is dist-only
 * here with zero consumers; wiring it in would be a yak-shave. Upgrade path
 * named above.
 *
 * `AGT_ISOLATION_AGENTS` (optional, comma-separated codeNames) scopes the
 * rollout to specific agents - this is the risk-tier rollout (ADR-0014 §4)
 * done by hand: empty/unset means "all agents on this host", otherwise only
 * the listed agents are sandboxed. Lets one agent run under Docker while its
 * neighbours stay on the host-spawn path during a phased migration.
 */
export function isolationMode(codeName?: string): 'none' | 'docker' {
  if (process.env.AGT_ISOLATION !== 'docker') return 'none';
  const allow = (process.env.AGT_ISOLATION_AGENTS ?? '')
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
  if (allow.length > 0 && (!codeName || !allow.includes(codeName))) return 'none';
  return 'docker';
}

/**
 * ENG-6579: infra domains every agent needs regardless of its TOOLS.md. The
 * per-agent allowlist is `these + (TOOLS.md network.allowlist_domains)`, and
 * squid denies everything else (deny-by-default). Leading-dot entries match a
 * domain and all its subdomains (squid `dstdomain` semantics).
 *
 * ponytail: Claude Code's telemetry endpoint (datadoghq.com) is deliberately
 * NOT here - deny-by-default silences outbound analytics, which is the safer
 * default for a sandboxed agent (spike saw `--print` work fine with it blocked,
 * 2026-06-16). Add it per-host via TOOLS.md if an operator wants telemetry.
 */
export const EGRESS_BASELINE_DOMAINS = [
  '.anthropic.com', // claude API
  'claude.ai', // subscription auth
  '.augmented.team', // the host/control-plane API
  '.slack.com', // channels (incl. wss)
  '.composio.dev', // composio MCP
];

/**
 * ENG-6579: egress is on only when (a) `AGT_EGRESS=allowlist` AND (b) the agent
 * is already Docker-isolated - the `--internal` network that makes the allowlist
 * unbypassable only exists on the Docker path. Reuses isolationMode's
 * `AGT_ISOLATION_AGENTS` scoping, so enabling egress for one sandboxed agent is
 * the same allowlist that sandboxed it.
 */
export function egressMode(codeName?: string): 'none' | 'allowlist' {
  if (process.env.AGT_EGRESS !== 'allowlist') return 'none';
  if (isolationMode(codeName) !== 'docker') return 'none';
  return 'allowlist';
}

/** The shape buildEgressAllowlist needs from a parsed TOOLS.md frontmatter. */
export type EgressToolsFrontmatter = {
  tools?: Array<{ network?: { allowlist_domains?: string[] } }>;
} | null | undefined;

// A bare hostname or leading-dot suffix (squid dstdomain semantics): dot-joined
// labels of [a-z0-9-], optional leading dot. No scheme, path, port, glob, or
// whitespace. Used to reject malformed TOOLS.md allowlist entries (ENG-6579).
const VALID_EGRESS_DOMAIN = /^\.?([a-z0-9-]+\.)+[a-z0-9-]+$/;

/**
 * ENG-6579: derive an agent's egress allowlist = baseline infra + the union of
 * every tool's `network.allowlist_domains`. This turns TOOLS.md (a document)
 * into an enforced control: a domain not declared for any tool is unreachable
 * from the sandbox.
 *
 * SECURITY: takes ALREADY-PARSED frontmatter, NOT a file path. The caller MUST
 * source this from the trusted control-plane refresh data
 * (`refreshData.tools.raw_content`), never from the agent's on-disk TOOLS.md -
 * that file lives in the agent's read-write mount, so a prompt-injected agent
 * could otherwise widen its own egress allowlist and defeat the containment
 * (CodeRabbit, PR #1528). The generated list is likewise written to a host-only
 * path the agent doesn't mount (see spawnSession).
 *
 * Fails open to baseline-only (not empty) when frontmatter is absent - an agent
 * that can still reach Anthropic + its host but no tool domains is a safe
 * degradation (it can report it's broken), whereas an empty allowlist bricks it.
 *
 * ponytail: doesn't honour `default_network_policy: allow` - egress mode is
 * always deny-by-default by design. Only enable it (via AGT_ISOLATION_AGENTS)
 * for agents whose TOOLS.md is allowlist-based. Documented in the README.
 *
 * Exported for unit tests.
 */
export function buildEgressAllowlist(toolsFrontmatter: EgressToolsFrontmatter): string[] {
  const domains = new Set<string>(EGRESS_BASELINE_DOMAINS);
  for (const tool of toolsFrontmatter?.tools ?? []) {
    for (const d of tool.network?.allowlist_domains ?? []) {
      if (typeof d !== 'string') continue;
      // Normalise (lowercase - domains are case-insensitive, avoids dupes) and
      // validate: a hostname or leading-dot suffix only. Rejects schemes, paths,
      // globs, spaces - garbage entries can't reach the squid allowlist file
      // (CodeRabbit, PR #1528).
      const norm = d.trim().toLowerCase();
      if (VALID_EGRESS_DOMAIN.test(norm)) domains.add(norm);
    }
  }
  return [...domains].sort();
}

/**
 * ENG-6579: host-only path for an agent's egress allowlist. Under
 * `~/.augmented/_egress/` - a dir the agent never mounts (see the mount set in
 * buildDockerRunCommand) - so the enforcement file is not agent-writable.
 */
export function egressAllowlistHostPath(codeName: string, homeDir?: string): string {
  const home = homeDir ?? (process.env.HOME?.trim() || homedir());
  return join(home, '.augmented', '_egress', `${codeName}.txt`);
}

/** ENG-6579: write the per-agent allowlist to its host-only path. Returns it. */
export function writeEgressAllowlist(codeName: string, domains: string[], homeDir?: string): string {
  const p = egressAllowlistHostPath(codeName, homeDir);
  mkdirSync(dirname(p), { recursive: true });
  writeFileSync(p, domains.join('\n') + '\n', { mode: 0o644 });
  return p;
}

// docker CLI calls from the supervisor are bounded so a stalled daemon can't
// block the manager tick indefinitely (CodeRabbit, PR #1528).
const EGRESS_DOCKER_TIMEOUT_MS = 10_000;

// Distinguish the BENIGN "sidecar isn't running" case (egress off, or not yet
// spawned - safe to ignore) from a real docker failure (daemon down, timeout)
// that must propagate so the caller doesn't advance the allowlist baseline and
// silently skip a retry (CodeRabbit, PR #1528).
function isNoSuchContainer(err: unknown): boolean {
  const e = err as { stderr?: Buffer | string; message?: string };
  const text = `${e?.stderr?.toString() ?? ''}${e?.message ?? ''}`;
  return /no such container|is not running/i.test(text);
}

/**
 * ENG-6579: tell a running squid sidecar to reload its allowlist WITHOUT a
 * restart. squid re-reads squid.conf + the mounted allowlist file on SIGHUP, so
 * a live session picks up purely-additive allowlist changes with zero agent
 * disruption. Use this only when nothing was removed - SIGHUP preserves
 * established CONNECT tunnels, so a removed domain's in-flight tunnel would
 * survive (see reloadOrRestartEgressSidecar / restartEgressSidecar).
 * Best-effort: a missing sidecar (egress off, or not yet spawned) is a no-op.
 */
export function reloadEgressSidecar(codeName: string): boolean {
  try {
    // execFileSync (not a shell string): codeName never reaches a shell, so it
    // can't inject even if it somehow contained shell metacharacters.
    execFileSync('docker', ['kill', '--signal=HUP', `agt-squid-${codeName}`], {
      stdio: 'pipe',
      timeout: EGRESS_DOCKER_TIMEOUT_MS,
    });
    return true; // policy actually applied to a running sidecar
  } catch (err) {
    // benign: no sidecar to reload -> not applied (caller shouldn't advance its
    // baseline). Real docker failures (daemon down, timeout) propagate so the
    // caller retries instead of advancing on a silently-failed reload.
    if (!isNoSuchContainer(err)) throw err;
    return false;
  }
}

/**
 * ENG-6579: restart the squid sidecar, which SEVERS all established connections
 * (incl. CONNECT tunnels) and re-reads the allowlist on start. Required when the
 * allowlist is NARROWED - a SIGHUP reload would leave an in-flight tunnel to a
 * now-removed domain alive, defeating an incident-response tightening (CodeRabbit,
 * PR #1528). Best-effort: a missing sidecar is a harmless no-op.
 */
export function restartEgressSidecar(codeName: string): boolean {
  try {
    execFileSync('docker', ['restart', `agt-squid-${codeName}`], {
      stdio: 'pipe',
      timeout: EGRESS_DOCKER_TIMEOUT_MS,
    });
    return true; // policy actually applied (sidecar restarted, re-read allowlist)
  } catch (err) {
    // benign: no sidecar to restart -> not applied. Real docker failures propagate.
    if (!isNoSuchContainer(err)) throw err;
    return false;
  }
}

/**
 * ADR-0014: build the `docker run` command that tmux exec's instead of the
 * bare wrapper, when isolation is on. The container's *mount namespace* is the
 * boundary - sibling `~/.augmented/<other>/` trees simply don't exist inside,
 * which closes T5 (cross-agent FS/secret access) in the kernel rather than via
 * the bypassable PreToolUse hook.
 *
 * Mounts (the whole filesystem story - see ADR-0014 §1):
 * - `~/.augmented/<codeName>`  rw  - the agent's own tree (wrapper, project,
 *   `.env.integrations`). Nothing else from `~/.augmented/` is visible.
 * - `~/.augmented/<agentId>`   rw  - the agent's agent-id-keyed state dir,
 *   where the direct-chat channel persists its session/lock
 *   (`direct-chat-session.json`, `direct-chat-channel.lock`). Distinct from the
 *   code-name dir above; omitting it makes direct-chat start a fresh session on
 *   every respawn and lose continuity (integrated-test finding on
 *   prod-demo-company-agt-demo-1, 2026-06-16).
 * - `~/.augmented/_mcp`        ro  - shared MCP server binaries (the one
 *   carve-out the isolation hook already whitelists).
 * - `~/.claude`                rw  - claude CLI creds/state directory.
 * - `~/.claude.json`           rw  - claude's global config file (MCP registry,
 *   onboarding state). Distinct from the `~/.claude/` dir above; claude reads
 *   BOTH, and omitting this one makes claude fall back to defaults (spike
 *   finding on prod-demo-company-agt-demo-1, 2026-06-16). Shared across agents
 *   today (all run as root, HOME=/root) - mounting it is parity, not a
 *   regression. Per-agent claude home is an open spike question.
 *
 * The claude/node toolchain is **baked into the image**, not mounted: on the
 * fleet host claude lives at `/usr/bin/claude` (global npm install), so
 * mounting its prefix would mean mounting the host's entire `/usr` over the
 * container's - clobbering its userland. Baking keeps the wrapper's absolute
 * `/usr/bin/claude` resolving inside the guest, and same-image containers
 * still share the read-only image-layer page cache across agents. Cost: the
 * image must be rebuilt when the fleet's claude version bumps (see README).
 *
 * Resource caps (`--memory`/`--cpus`) end the runaway-agent-starves-neighbours
 * failure mode. Defaults overridable per-host; risk-tier-keyed caps come with
 * the feature-flag wiring.
 *
 * Exported for unit tests.
 */
export function buildDockerRunCommand(args: {
  codeName: string;
  agentId: string;
  wrapperPath: string;
  projectDir: string;
  homeDir: string;
  runId?: string;
  passApiKey: boolean;
  /**
   * ENG-7152: when true, name-forward the OpenRouter ANTHROPIC_* vars
   * (ANTHROPIC_BASE_URL/AUTH_TOKEN/MODEL/SMALL_FAST_MODEL) from the session-shell
   * env into the container — same ps-hiding posture as passApiKey. Mutually
   * exclusive with passApiKey (OpenRouter mode supersedes api_key auth).
   */
  passOpenRouter?: boolean;
  /**
   * ENG-6579: when present, run the agent on a per-agent `--internal` network
   * (no route off-net) and force its traffic through a squid sidecar that
   * enforces the allowlist file at `allowlistHostPath`. Absent => no egress
   * control (unchanged shared-bridge networking).
   */
  egress?: { allowlistHostPath: string };
}): string {
  const { codeName, agentId, wrapperPath, projectDir, homeDir, runId, passApiKey, passOpenRouter, egress } = args;
  // POSIX single-quote escaping. Single quotes prevent ALL shell expansion -
  // $(...), backticks, $VAR - unlike JSON.stringify's double quotes, which still
  // expand them. An embedded single quote becomes '\'' (close, escaped, reopen).
  // Defence in depth for the docker command string (CodeRabbit, PR #1528).
  const q = (s: string) => `'${s.replace(/'/g, `'\\''`)}'`;

  const agentDir = join(homeDir, '.augmented', codeName);
  const agentIdDir = join(homeDir, '.augmented', agentId);
  const mcpDir = join(homeDir, '.augmented', '_mcp');
  const claudeHome = join(homeDir, '.claude');
  const claudeJson = join(homeDir, '.claude.json');

  const mounts = [
    `-v ${q(`${agentDir}:${agentDir}`)}`,
    `-v ${q(`${agentIdDir}:${agentIdDir}`)}`,
    `-v ${q(`${mcpDir}:${mcpDir}:ro`)}`,
    `-v ${q(`${claudeHome}:${claudeHome}`)}`,
    `-v ${q(`${claudeJson}:${claudeJson}`)}`,
  ];

  const image = process.env.AGT_ISOLATION_IMAGE || 'agt-runtime:latest';
  // ENG-6670: a real agent runs claude + ~6 stdio MCP servers (each a ~60-110MB
  // node process: augmented, direct-chat, slack, telegram, msteams, cloud-broker)
  // + transient npx. The old 512m default OOM-killed the heaviest/last-to-start
  // servers - and eventually claude itself - inside the cgroup, with NO stderr
  // (SIGKILL), which read as "MCPs don't start in-container" (kernel-confirmed on
  // agt-aws-1: `Memory cgroup out of memory: Killed process ... (claude)`). 2g
  // fits the working set (~1-1.5GB) with headroom for tool execution. Sizing note
  // for operators: the sum of per-agent caps must fit host RAM (agt-aws-1 is 15Gi)
  // - override per host/agent via AGT_ISOLATION_MEMORY as the allowlist grows.
  const memory = process.env.AGT_ISOLATION_MEMORY || '2g';
  const cpus = process.env.AGT_ISOLATION_CPUS || '1.0';
  // `--pids-limit` caps process count so a fork bomb in one sandbox can't
  // exhaust host-wide PIDs and take down its neighbours (red-team finding,
  // 2026-06-16). ~300 procs is plenty for claude + its MCP servers.
  const pids = process.env.AGT_ISOLATION_PIDS || '512';

  const envArgs: string[] = [`-e ${q(`HOME=${homeDir}`)}`];
  // ENG-6670: point npm/npx at a PER-AGENT writable cache inside the agent's own
  // (already rw-mounted) tree. npx-based MCP servers (cloud-broker:
  // `npx -y @integrity-labs/cloud-broker@latest`) MUST write _npx/_logs to a
  // cache dir; the default ~/.npm isn't in the container's mount set. Without a
  // writable cache npx fails ("rofs"/EROFS), cloud-broker never starts, and the
  // presence-reaper then restarts the session every poll - a flap loop that also
  // SIGTERMs the slower channel MCPs (slack/telegram) as collateral (root-caused
  // on agt-aws-1, 2026-06-18). Per-agent (not the shared host ~/.npm) preserves
  // the T5 cross-agent boundary - one agent can't poison another's npx cache
  // (ADR-0014). Lives in the persistent agent tree, so it's warm after the first
  // spawn. Ceiling: under egress filtering `npx ...@latest` still needs a registry
  // version-check - pin/pre-warm the version then (track w/ ENG-6579).
  envArgs.push(`-e ${q(`npm_config_cache=${join(agentDir, '.npm-cache')}`)}`);
  // Name-only forward keeps the API key off the docker argv (it's read from
  // the tmux session shell's env, set via `tmux new-session -e`). Same
  // ps-hiding posture as ENG-4717.
  if (passApiKey) envArgs.push('-e ANTHROPIC_API_KEY');
  // ENG-7152: OpenRouter mode forwards (name-only, same ps-hiding posture). The
  // values come from the tmux session-shell env (`tmux new-session -e`), so the
  // raw token never crosses the docker argv. KEEP IN SYNC with the openRouterMode
  // block in spawnSession + the probe-env mirror below.
  if (passOpenRouter) {
    envArgs.push('-e ANTHROPIC_BASE_URL');
    envArgs.push('-e ANTHROPIC_AUTH_TOKEN');
    envArgs.push('-e ANTHROPIC_MODEL');
    envArgs.push('-e ANTHROPIC_SMALL_FAST_MODEL');
  }
  if (runId) envArgs.push(`-e ${q(`AGT_RUN_ID=${runId}`)}`);
  // ENG-6670: plumb the agent's host-exchange auth into the container. Channel
  // MCPs (slack, telegram) hit the host `/host/exchange` endpoint to mint a
  // short-TTL JWT, authenticating with AGT_API_KEY/AGT_HOST that Claude Code
  // substitutes into .mcp.json from the *claude process env* (ADR-0006). The
  // `docker run` boundary severs that inheritance, so in-container the `${...}`
  // placeholders resolve to EMPTY -> /host/exchange returns 401 "API key not
  // found" -> slack/telegram tear down on a cycle -> the presence-reaper
  // (correctly) restarts the session => the flap. teams is unaffected (it auths
  // to the Bot Framework via AAD creds in the wrapper-sourced .env.integrations,
  // not /host/exchange), and host-spawn agents inherit these from the manager
  // env - which is why this bites ONLY isolated agents (measured on agt-aws-1,
  // 2026-06-18: idle CPU, cloud-broker healthy, yet slack/telegram cycled out
  // on the /host/exchange 401). Name-only forward for AGT_API_KEY/AGT_HOST keeps
  // the secret off the docker argv (read from the tmux session shell's env),
  // same posture as ANTHROPIC_API_KEY above; AGT_AGENT_ID is non-secret and
  // already known here, so pass it by value.
  envArgs.push('-e AGT_API_KEY');
  envArgs.push('-e AGT_HOST');
  envArgs.push(`-e ${q(`AGT_AGENT_ID=${agentId}`)}`);
  // ENG-6818: turn ON the in-session direct-chat doorbell+pull rail for isolated
  // agents. The manager's default delivery is `tmux send-keys` into the pane, but
  // under isolation the pane runs `docker run -it` and keystrokes don't reach the
  // in-container claude — so direct chat silently dropped (3 dots, no reply). The
  // doorbell rail is container-safe: the MCP polls /host/direct-chat over HTTP and
  // watches the doorbell file in the bind-mounted ~/.augmented/<agentId> dir. The
  // manager side rings the doorbell for isolated agents (processDirectChatMessage).
  envArgs.push(`-e AGT_DIRECT_CHAT_DOORBELL_ENABLED=true`);
  // ENG-6724: mark the container so the in-container restart paths (request_restart
  // MCP tool, Slack/Telegram `/restart`) actuate host-side via the API instead of
  // writing `~/.augmented/restart-flags/<codeName>.flag`. That dir is NOT mounted
  // (and can't be - it's shared across all agents, so a bind-mount would breach the
  // T5/ADR-0014 cross-agent boundary), so an in-container flag write lands on the
  // --rm overlay and the host reaper never sees it (silent no-op). Inherited by
  // claude and every MCP child via the container env; read by isInContainer().
  envArgs.push(`-e AGT_IN_CONTAINER=true`);

  // ENG-6579: egress allowlist. The agent joins a per-agent `--internal`
  // network (Docker gives it no route off-net), and a squid sidecar - bridged
  // to both that internal net and a normal egress net - is the only path out.
  // squid filters HTTPS by the hostname in the CONNECT line (no TLS intercept,
  // no MITM) against the mounted allowlist; deny-by-default. Validated on
  // prod-demo-company-agt-demo-1, 2026-06-16 (see host-infra/agt-runtime/squid).
  const egressImage = process.env.AGT_EGRESS_IMAGE || 'agt-squid:latest';
  const internalNet = `agt-net-${codeName}`;
  const squidName = `agt-squid-${codeName}`;
  const networkArgs: string[] = [];
  let egressSetup = '';
  if (egress) {
    networkArgs.push(`--network ${internalNet}`);
    // The agent talks to the proxy by container name (resolved by Docker's
    // embedded DNS on the user-defined net). NO_PROXY keeps the hop TO the
    // proxy - and loopback - direct, so the client doesn't proxy-to-itself.
    const proxyUrl = `http://${squidName}:3128`;
    envArgs.push(`-e ${q(`HTTPS_PROXY=${proxyUrl}`)}`);
    envArgs.push(`-e ${q(`HTTP_PROXY=${proxyUrl}`)}`);
    envArgs.push(`-e ${q(`NO_PROXY=${squidName},localhost,127.0.0.1`)}`);
    // Tear down both containers first so the internal-net check below can
    // recreate the network if needed (a network can't be removed while attached).
    // `agt-egress` is shared (squids can't be reached by agents - agents are
    // only on their own internal net).
    // `&&`-chained (CodeRabbit, PR #1528): every fail-closed step must succeed
    // before the agent execs (see the return below) - a failure here must abort,
    // never fall through to an agent running without its proxy / internal net.
    // The verify-or-repair is wrapped in `{ ... ; }` so its inner `||` can't
    // tangle with the outer `&&` (shell gives them equal precedence).
    egressSetup = [
      `docker rm -f ${squidName} agt-${codeName} >/dev/null 2>&1 || true`,
      `docker network create agt-egress >/dev/null 2>&1 || true`,
      // FAIL-CLOSED: a stale or hand-created `agt-net-<codeName>` that is NOT
      // internal would give the agent a route off-net, bypassing the proxy.
      // Don't trust create-if-absent - verify the Internal flag and rebuild the
      // network when it's missing or wrong.
      `{ [ "$(docker network inspect -f '{{.Internal}}' ${internalNet} 2>/dev/null)" = "true" ] || ` +
        `{ docker network rm ${internalNet} >/dev/null 2>&1 || true; docker network create --internal ${internalNet} >/dev/null; }; }`,
      // squid processes untrusted agent traffic - give it the same hardening as
      // the agent container (drop all caps, block privilege escalation). squid
      // binds 3128 (>1024) and needs no capabilities; verified it still boots.
      `docker run -d --name ${squidName} --network ${internalNet} --restart unless-stopped --memory 128m ` +
        `--cap-drop ALL --security-opt no-new-privileges ` +
        `-v ${q(`${egress.allowlistHostPath}:/etc/squid/allowlist.txt:ro`)} ${q(egressImage)} >/dev/null`,
      `docker network connect agt-egress ${squidName} >/dev/null 2>&1`,
    ].join(' && ');
  }

  // `-it`: tmux's pane already owns a real pty; -t gives claude a tty inside
  // the container so channel/interactive assumptions hold (verify in the
  // spike - the TTY handoff across the docker boundary is open question #2).
  // `--rm` + the pre-run force-remove keep respawns idempotent: a stale
  // container from a crashed session can't block the new one on name reuse.
  // `exec` so the container process becomes the pane's process (clean signals).
  const runCmd = [
    'exec docker run --rm -it',
    `--name agt-${codeName}`,
    `--memory ${memory}`,
    `--cpus ${cpus}`,
    `--pids-limit ${pids}`,
    // Defence in depth (red-team 2026-06-16): drop all Linux capabilities and
    // block privilege escalation. claude + node MCP servers need none - verified
    // booting clean under these. The mount namespace is the primary boundary;
    // these shrink what a container-escape CVE could reach if it ever landed.
    '--cap-drop ALL',
    '--security-opt no-new-privileges',
    ...networkArgs,
    ...mounts,
    `-w ${q(projectDir)}`,
    ...envArgs,
    q(image),
    q(wrapperPath),
  ].join(' ');

  // egress on: the whole setup must succeed before the agent execs - if any
  // fail-closed step fails, `&&` aborts and the agent never starts WITHOUT its
  // proxy / internal net (fail-closed). egress off: just clear any stale
  // container and run as before.
  return egress
    ? `${egressSetup} && ${runCmd}`
    : `docker rm -f agt-${codeName} >/dev/null 2>&1; ${runCmd}`;
}

/**
 * ENG-4717: write the wrapper script that the persistent tmux session
 * exec's instead of putting `KEY=VALUE claude ...` directly on the
 * tmux command line. The wrapper sources `.env.integrations` (mode
 * 0600) inside the spawned shell, so secrets land in the exec'd
 * claude process's env without ever crossing the argv boundary that
 * `ps -eo command` reads from.
 *
 * Returns the wrapper path. Always overwrites — kept idempotent so
 * a manager respawn after a credential rotation picks up the new
 * .env.integrations contents on the next session start.
 *
 * Exported for unit tests; production callers go through startSession.
 */
export function writePersistentClaudeWrapper(args: {
  projectDir: string;
  claudeBin: string;
  initPrompt: string;
  claudeArgsJoined: string;
}): string {
  const { projectDir, claudeBin, initPrompt, claudeArgsJoined } = args;
  const envIntegrationsPath = join(projectDir, '.env.integrations');
  const wrapperPath = join(projectDir, '.claude', 'persistent-claude.sh');
  const wrapperLines = [
    '#!/usr/bin/env bash',
    'set -e',
    // IS_SANDBOX=1 lets claude run under root/sudo with
    // --dangerously-skip-permissions on dedicated EC2 hosts.
    'export IS_SANDBOX=1',
  ];
  if (existsSync(envIntegrationsPath)) {
    // `set -a` exports every variable assigned by `source`; `set +a`
    // restores the prior state. Anything in .env.integrations becomes
    // an environment variable for the exec'd claude process.
    wrapperLines.push(
      'set -a',
      `source ${JSON.stringify(envIntegrationsPath)}`,
      'set +a',
    );
  }
  // ENG-5353: when initPrompt is empty (resumed session), omit the empty
  // positional entirely. Passing `""` as a positional to claude would still
  // append a blank user turn to the resumed transcript and trip the same
  // upstream regression we're trying to avoid.
  const initPromptArg = initPrompt ? `${JSON.stringify(initPrompt)} ` : '';
  wrapperLines.push(
    `exec ${JSON.stringify(claudeBin)} ${initPromptArg}${claudeArgsJoined}`,
  );
  mkdirSync(join(projectDir, '.claude'), { recursive: true });
  // 0700: only the agent process owner can read/execute. The wrapper
  // doesn't contain secrets itself (it sources them from the 0600
  // file) but a hostile reader could still see which env vars get
  // loaded — enough leakage to lock down. The mode option on
  // writeFileSync is only honoured when the file is *created*, so we
  // chmodSync afterwards to enforce 0700 on overwrites too (the
  // common case after the first respawn).
  writeFileSync(wrapperPath, wrapperLines.join('\n') + '\n', { mode: 0o700 });
  chmodSync(wrapperPath, 0o700);
  return wrapperPath;
}

/**
 * Collect MCP server names from the project .mcp.json to build the
 * --allowedTools pattern for tool isolation.
 */
function collectMcpServerNames(mcpConfigPath: string): string[] {
  if (!existsSync(mcpConfigPath)) return [];
  try {
    const data = JSON.parse(readFileSync(mcpConfigPath, 'utf-8'));
    const servers = data.mcpServers as Record<string, unknown> | undefined;
    return servers ? Object.keys(servers) : [];
  } catch {
    return [];
  }
}

// ---------------------------------------------------------------------------
// Types and state
// ---------------------------------------------------------------------------

export interface PersistentSessionConfig {
  codeName: string;
  agentId: string;
  projectDir: string;
  mcpConfigPath: string;
  claudeMdPath: string;
  channels: string[];
  devChannels: string[];
  apiHost?: string;
  /**
   * Operator-configured Claude Code auth mode. 'subscription' (default) runs
   * `syncClaudeCredsToRoot()` so claude finds OAuth creds under /root/.claude.
   * 'api_key' puts ANTHROPIC_API_KEY into the spawn env AND deletes any
   * stored OAuth creds so the two auth paths are mutually exclusive.
   */
  claudeAuthMode?: 'subscription' | 'api_key' | 'openrouter';
  /** Decrypted Anthropic API key. Only used when claudeAuthMode === 'api_key'. */
  anthropicApiKey?: string | null;
  /**
   * ENG-5631: the agent's resolved primary model as a full platform model
   * name (e.g. `claude-sonnet-4-6`, possibly with an `openrouter/anthropic/`
   * prefix). The launcher reduces it to a family alias via `claudeModelAlias`
   * and passes `--model <alias>` to the `claude` spawn — without this, a
   * subscription agent ignores its platform model setting and runs the auth
   * tier's default (Opus 4.7 on Max). When empty/unknown, no `--model` flag is
   * passed and Claude Code uses the tier default.
   */
  primaryModel?: string | null;
  /**
   * ENG-7152: OpenRouter BYO-model mode. When present (the API delivered an
   * `openrouter` block on /host/refresh — flag on + the agent has a stored
   * OpenRouter key), the launcher points Claude Code at OpenRouter's native
   * Anthropic endpoint instead of the operator subscription: it injects
   * ANTHROPIC_BASE_URL/ANTHROPIC_AUTH_TOKEN/ANTHROPIC_MODEL (and, when set,
   * ANTHROPIC_SMALL_FAST_MODEL) via tmux `-e`, purges any OAuth creds (same as
   * api_key mode), and SKIPS the `--model <alias>` path (the model is the
   * provider/model id carried in `model`, not a Claude family alias). Supersedes
   * claudeAuthMode for this agent. Null/undefined ⇒ normal subscription/api_key.
   */
  openRouter?: {
    /** Decrypted OpenRouter inference key (sk-or-…), used as ANTHROPIC_AUTH_TOKEN. */
    authToken: string;
    /** Bare `provider/model` id (openrouter/ prefix already stripped), → ANTHROPIC_MODEL. */
    model: string;
    /** Bare provider/model id for the background/fast model, → ANTHROPIC_SMALL_FAST_MODEL. Null ⇒ omit. */
    smallFastModel: string | null;
  } | null;
  /**
   * ENG-5051: per-session run UUID. When set, exported into the tmux
   * session env so Claude Code's ${AGT_RUN_ID} placeholder substitution
   * in .mcp.json resolves to a real run, unblocking the cloud-broker
   * MCP which 400s when AGT_RUN_ID is the literal placeholder. Minted
   * by manager-worker via /host/runs/start at session-spawn time.
   * When unset, Claude leaves the literal placeholder (legacy behaviour);
   * cloud-broker startup fails and the agent must mint a run by hand.
   */
  runId?: string | null;
  /**
   * ENG-5371: IANA timezone (e.g. `Australia/Melbourne`) used to compute
   * the daily-session day boundary. When omitted, the daily-session
   * helper falls back to host-local (UTC on Lambda/EC2), which is the
   * pre-ENG-5371 behaviour. The manager resolves this from the same
   * source as ENG-5363's channel-MCP `TZ` env var (`teamSettings.timezone`).
   */
  agentTimezone?: string | null;
  /**
   * ENG-6579: the agent's egress allowlist (baseline + TOOLS.md
   * `allowlist_domains`), derived by the manager from the TRUSTED control-plane
   * refresh data - NOT the agent's on-disk TOOLS.md (which is agent-writable).
   * Consumed only when egress is enabled (AGT_EGRESS=allowlist + Docker
   * isolation). When omitted, spawnSession falls back to baseline-only.
   */
  egressAllowlist?: string[];
  log: (msg: string) => void;
}

export interface PersistentSession {
  codeName: string;
  startedAt: number | null;
  restartCount: number;
  status: 'starting' | 'running' | 'stopped' | 'crashed';
  /**
   * ENG-4659: the UUID we passed to claude on the most recent spawn.
   * Set right after `tmux new-session` succeeds. This is the value the
   * recovery hook compares against `lastFailureSessionId` to detect
   * "same UUID failed twice in a row" — they used to be the same
   * field, which made the gate compare a value to itself and always
   * trip after the first failure (CodeRabbit catch).
   */
  currentSessionId: string | null;
  /**
   * ENG-4659: tail of the tmux pane the last time the session
   * transitioned to crashed. Captured by readPaneLogTail() when the
   * healthcheck detects no tmux session, so the next "unhealthy" log
   * line carries the actual error Claude printed before exiting.
   * Cleared on the next successful spawn.
   */
  lastFailureTail: string | null;
  /**
   * ENG-4659: the session UUID that was in flight when the previous
   * failure was captured. Used to detect "same UUID failing repeatedly"
   * — when the just-failed `currentSessionId` matches this, we
   * increment `consecutiveSameUuidFailures`; when they differ (or this
   * is null), we reset to 1.
   */
  lastFailureSessionId: string | null;
  /**
   * Count of consecutive failures with the same `lastFailureSessionId`.
   * Reset on a successful spawn or when the session UUID rotates. The
   * "Session ID already in use" rotation gate fires at >= 2 to avoid
   * losing today's history on a single flaky failure.
   */
  consecutiveSameUuidFailures: number;
  /**
   * ENG-5371: the agent's IANA timezone (mirrored from
   * PersistentSessionConfig.agentTimezone on the most recent spawn).
   * Persisted on the session object so `prepareForRespawn` can pass it
   * to `rotateDailySession` without re-resolving from team settings.
   * `null` keeps host-local behaviour (the original ENG-4642 default).
   */
  agentTimezone: string | null;
}

const sessions = new Map<string, PersistentSession>();

// ---------------------------------------------------------------------------
// Pane-log capture (ENG-4659)
//
// The tmux child we spawn is detached (`new-session -d`) so its stdio is
// closed before claude even prints. To capture claude's output for
// post-mortem we call `tmux pipe-pane -o` immediately after creating the
// session, redirecting all pane output to a per-agent log file. On
// unhealthy detection we read the tail of that file and surface it in
// the log + scan it for known failure signatures.
// ---------------------------------------------------------------------------

const PANE_LOG_DIR = join(homedir(), '.augmented');
const PANE_TAIL_LINES = 20;

export function paneLogPath(codeName: string): string {
  return join(PANE_LOG_DIR, codeName, 'pane.log');
}

function setupPaneLog(tmuxSession: string, codeName: string, log: (msg: string) => void): void {
  const logPath = paneLogPath(codeName);
  try {
    mkdirSync(dirname(logPath), { recursive: true });
    // Append a spawn marker rather than truncating — the previous
    // crash's output is exactly what an operator opening the file
    // wants to see, so wiping it on every respawn defeats the
    // post-mortem use case (CodeRabbit catch). The in-memory tail
    // captured at unhealthy-detection time still uses the most recent
    // lines so logs reflect the *current* failure correctly; the
    // on-disk file is the long-form record.
    appendFileSync(
      logPath,
      `\n--- spawn ${new Date().toISOString()} (session ${tmuxSession}) ---\n`,
      'utf-8',
    );
    // Quote the path for the shell-cat invocation tmux runs.
    execSync(
      `tmux pipe-pane -o -t ${tmuxSession} 'cat >> ${logPath.replace(/'/g, `'\\''`)}'`,
      { stdio: 'ignore' },
    );
  } catch (err) {
    // Pane logging is diagnostic-only. A failure here just means the
    // next unhealthy log line won't carry the tail — the session still
    // runs. Don't propagate.
    log(`[persistent-session] pipe-pane setup failed for '${codeName}': ${(err as Error).message}`);
  }
}

export function readPaneLogTail(codeName: string, lines: number = PANE_TAIL_LINES): string | null {
  const logPath = paneLogPath(codeName);
  if (!existsSync(logPath)) return null;
  try {
    const raw = readFileSync(logPath, 'utf-8');
    if (!raw) return null;
    // Strip ANSI escape sequences so the captured tail is
    // human-readable in operator-facing logs.
    // eslint-disable-next-line no-control-regex
    const stripped = raw.replace(/\x1b\[[0-9;?]*[A-Za-z]/g, '');
    const all = stripped.split('\n').filter((l) => l.length > 0);
    return all.slice(-lines).join('\n');
  } catch {
    return null;
  }
}

/**
 * Detect known Claude failure signatures from the captured pane tail.
 * Right now we recognise just one — "Session ID already in use" — which
 * was responsible for the multi-hour scout outage that motivated this
 * code (see ENG-4659). Returns 'unknown' when the tail has no signal we
 * can act on.
 */
type FailureSignature = 'session_id_in_use' | 'unknown';

function detectFailureSignature(tail: string | null): FailureSignature {
  if (!tail) return 'unknown';
  if (/Session ID .* is already in use/i.test(tail)) return 'session_id_in_use';
  return 'unknown';
}

/**
 * Pre-spawn recovery hook (ENG-4659). Called by the manager between
 * detecting an unhealthy session and respawning.
 *
 * Rotation gate: >= 2 consecutive failures with the same UUID rotates
 * the daily-session UUID so the next spawn cold-starts fresh.
 *
 * ENG-6039: the gate is signature-agnostic. It originally required the
 * 'session_id_in_use' pane signature, which is exactly why ENG-5397 had
 * to rip out --resume — a transcript poisoned any other way (ENG-5353's
 * 400 role 'system' printed no recognisable signature) was resumed
 * forever and took the agent silent for hours. With resume back on the
 * spawn path (same-day respawns), ANY repeated same-UUID failure must
 * rotate. Cost of over-firing: one day's conversation continuity on a
 * doubly-crashed-but-healthy transcript. Cost of under-firing: a
 * permanently wedged agent. We rotate.
 *
 * Returns a short human-readable summary of any action taken (or
 * `null` if no action was warranted), suitable for inclusion in the
 * "Session unhealthy" log line.
 */
export function prepareForRespawn(codeName: string): string | null {
  const session = sessions.get(codeName);
  if (!session) return null;
  const signature = detectFailureSignature(session.lastFailureTail);
  if (session.consecutiveSameUuidFailures >= 2) {
    // Capture the count BEFORE resetting so the operator-facing log
    // line carries the actual streak length. The original code reset
    // first and then read 0 (CodeRabbit catch).
    const failureCount = session.consecutiveSameUuidFailures;
    // ENG-5371: rotate at the agent's configured timezone day boundary
    // when one is set; falls back to host-local otherwise.
    const newId = rotateDailySession(
      codeName,
      new Date(),
      session.agentTimezone ?? undefined,
    );
    // Reset counter — fresh UUID, fresh slate.
    session.consecutiveSameUuidFailures = 0;
    session.lastFailureSessionId = null;
    return `rotated daily-session UUID to ${newId} after ${failureCount} consecutive failures on the same UUID (signature=${signature})`;
  }
  return null;
}

/**
 * ENG-6153: force the NEXT spawn to cold-start a fresh session (no --resume)
 * after the manager detects a wedged-but-alive session.
 *
 * A wedge isn't a crash, so `isSessionHealthy` never flips it to unhealthy and
 * the `prepareForRespawn` rotation gate never trips — a manual restart just
 * `--resume`s the same stuck transcript and re-wedges. This rotates today's
 * daily-session UUID so `resolveSessionSpawnDecision` falls to
 * `rotated-missing-transcript` → fresh `--session-id`. The OLD transcript stays
 * on disk (rotation only re-pins the UUID), preserved for forensics. Resets the
 * same-UUID failure counter so the fresh UUID starts with a clean slate.
 *
 * The caller is expected to then tear down the wedged session (tmux
 * kill-session + stopPersistentSessionAndForgetMcpBaseline) so the ensure pass
 * respawns onto the rotated UUID. Returns the new session id for the log line.
 */
export function rotateSessionForWedge(codeName: string, now: Date = new Date()): string {
  const session = sessions.get(codeName);
  const newId = rotateDailySession(codeName, now, session?.agentTimezone ?? undefined);
  if (session) {
    session.consecutiveSameUuidFailures = 0;
    session.lastFailureSessionId = null;
  }
  return newId;
}

/**
 * Read the captured pane tail + restart counter for the manager to
 * include in its unhealthy log. Read-only; doesn't mutate session
 * state.
 */
export function getLastFailureContext(codeName: string): {
  tail: string | null;
  signature: FailureSignature;
  consecutiveSameUuid: number;
  restartCount: number;
} {
  const session = sessions.get(codeName);
  return {
    tail: session?.lastFailureTail ?? null,
    signature: detectFailureSignature(session?.lastFailureTail ?? null),
    consecutiveSameUuid: session?.consecutiveSameUuidFailures ?? 0,
    restartCount: session?.restartCount ?? 0,
  };
}

// ---------------------------------------------------------------------------
// Spawn session binding (ENG-6039)
// ---------------------------------------------------------------------------

export interface SessionSpawnDecision {
  /** Claude CLI flag the spawn should pass the UUID with. */
  flag: '--resume' | '--session-id';
  sessionId: string;
  /** Why this binding was chosen — included in the spawn log line. */
  reason: 'resume-today' | 'fresh-new-day' | 'rotated-missing-transcript' | 'resume-disabled';
}

/**
 * ENG-6039: decide how the next spawn binds to a Claude session.
 *
 * Re-uses today's session across respawns: resolves the per-day UUID
 * (agent-timezone day boundary, ENG-5371) and resumes it via --resume
 * when claude has already written a transcript for it; otherwise
 * cold-starts via --session-id.
 *
 *   - transcript exists for today's pinned UUID → `--resume` it
 *   - first spawn of the (agent-tz) day → fresh `--session-id`
 *   - same day but no transcript (previous spawn died before claude
 *     materialised the JSONL) → rotate to a new UUID and `--session-id`
 *     it; re-passing the old UUID risks "Session ID already in use"
 *     (the ENG-4659 wedge)
 *
 * History: ENG-5397 removed --resume entirely after a poisoned
 * transcript (ENG-5353's 400 role 'system' on 2.1.139) persisted
 * across respawns and took Stirling silent for hours on 2026-05-22.
 * The actual gap wasn't resume itself — it was that the rotation gate
 * in prepareForRespawn() only fired on the 'session_id_in_use' pane
 * signature, so a transcript failing any *other* way was resumed
 * forever. That gate is now signature-agnostic (>= 2 consecutive
 * failures on the same UUID rotates it), capping a poisoned transcript
 * at two respawn attempts before the next spawn cold-starts fresh.
 *
 * Escape hatch: AGT_DISABLE_SESSION_RESUME=1 restores the ENG-5397
 * fresh-random-UUID-per-spawn behaviour (continuity then comes solely
 * from the SessionStart orient hook's injected context).
 */
export function resolveSessionSpawnDecision(args: {
  codeName: string;
  projectDir: string;
  agentTimezone?: string;
  now?: Date;
}): SessionSpawnDecision {
  const { codeName, projectDir, agentTimezone } = args;
  const now = args.now ?? new Date();
  const disableFlag = process.env['AGT_DISABLE_SESSION_RESUME'];
  const resumeDisabled = disableFlag === '1' || disableFlag?.toLowerCase() === 'true';
  if (resumeDisabled) {
    return { flag: '--session-id', sessionId: randomUUID(), reason: 'resume-disabled' };
  }
  const daily = getOrCreateDailySession(codeName, now, agentTimezone);
  if (!daily.isNew && sessionFileExists(projectDir, daily.sessionId)) {
    return { flag: '--resume', sessionId: daily.sessionId, reason: 'resume-today' };
  }
  if (daily.isNew) {
    return { flag: '--session-id', sessionId: daily.sessionId, reason: 'fresh-new-day' };
  }
  return {
    flag: '--session-id',
    sessionId: rotateDailySession(codeName, now, agentTimezone),
    reason: 'rotated-missing-transcript',
  };
}

/**
 * ENG-5927 (PR-4): path of the per-agent direct-chat session-state file the
 * manager writes at spawn and the in-session direct-chat MCP reads at startup.
 * Keyed on the agent UUID to match the MCP's `DIRECT_CHAT_AGENT_DIR`
 * (`~/.augmented/<AGT_AGENT_ID>/`) — same convention as the PR-2 doorbell file.
 */
export function directChatSessionStatePath(agentId: string): string {
  return join(homedir(), '.augmented', agentId, 'direct-chat-session.json');
}

export interface DirectChatSessionState {
  /** true ⇒ a genuinely fresh transcript (--session-id); false ⇒ --resume. */
  fresh: boolean;
  /** The bound Claude session UUID. */
  sessionId: string;
  /** Epoch ms the session was (re)spawned — the MCP's resume-aware `since`. */
  startedAtMs: number;
  /**
   * ENG-7263: the `.mcp.json` `mcpServers` keys present when this session
   * spawned (the `--mcp-config` + `--strict-mcp-config` it launched with). This
   * is the authoritative "which MCP servers did the running session actually
   * load" set - the session-tool-bind probe gates HTTP `bound` on membership
   * here instead of the `.mcp.json` file mtime, which the manager bumps
   * post-spawn (re-render / token refresh) and so produced false negatives.
   * Optional: absent on sessions spawned before this field existed (the probe
   * then falls back conservatively to `unknown` until the next respawn).
   */
  mcpServerKeys?: string[];
}

/**
 * Read the persisted per-agent direct-chat session state (best-effort). Used by
 * the session-tool-bind probe - both the manager and the standalone
 * `agt probe-tools` process - to learn the session's spawn-time MCP server set.
 * Returns null when the file is absent/unparseable.
 */
export function readDirectChatSessionState(agentId: string): DirectChatSessionState | null {
  try {
    const parsed = JSON.parse(readFileSync(directChatSessionStatePath(agentId), 'utf-8')) as unknown;
    if (!parsed || typeof parsed !== 'object') return null;
    const state = parsed as DirectChatSessionState;
    // Defensive: a parseable-but-malformed file could carry `mcpServerKeys` as a
    // non-array, which would make a downstream `new Set(...)` throw on a
    // non-iterable. Normalize to string[] | undefined (CodeRabbit, PR #2893).
    state.mcpServerKeys = Array.isArray(state.mcpServerKeys)
      ? state.mcpServerKeys.filter((k): k is string => typeof k === 'string')
      : undefined;
    return state;
  } catch {
    return null;
  }
}

/**
 * Publish session freshness for the in-session direct-chat MCP so its doorbell
 * replay is resume-aware (ADR-0020 §Decision 4): only a fresh session may drain
 * the pending backlog; a --resume session must not re-deliver pre-restart
 * messages into a transcript that already handled them. Best-effort — a write
 * failure degrades the MCP to its conservative default (treat as resumed: no
 * backlog drain, deliver only brand-new messages).
 */
export function writeDirectChatSessionState(
  agentId: string,
  state: DirectChatSessionState,
): void {
  const p = directChatSessionStatePath(agentId);
  mkdirSync(dirname(p), { recursive: true });
  writeFileSync(p, JSON.stringify(state));
}

// ---------------------------------------------------------------------------
// Session lifecycle (tmux-based)
// ---------------------------------------------------------------------------

export function startPersistentSession(config: PersistentSessionConfig): PersistentSession {
  const existing = sessions.get(config.codeName);
  if (existing && existing.status === 'running') {
    return existing;
  }

  // Backoff on repeated crashes
  const restartCount = existing?.restartCount ?? 0;
  if (existing?.status === 'crashed' && existing.startedAt) {
    const backoffMs = Math.min(5000 * Math.pow(2, restartCount), 60_000);
    if (Date.now() - existing.startedAt < backoffMs) {
      return existing;
    }
  }

  const session: PersistentSession = {
    codeName: config.codeName,
    startedAt: null,
    restartCount,
    status: 'starting',
    currentSessionId: existing?.currentSessionId ?? null,
    lastFailureTail: existing?.lastFailureTail ?? null,
    lastFailureSessionId: existing?.lastFailureSessionId ?? null,
    consecutiveSameUuidFailures: existing?.consecutiveSameUuidFailures ?? 0,
    agentTimezone: config.agentTimezone ?? null,
  };
  sessions.set(config.codeName, session);

  spawnSession(config, session);
  return session;
}

function spawnSession(config: PersistentSessionConfig, session: PersistentSession): void {
  const { codeName, projectDir, mcpConfigPath, claudeMdPath, channels, devChannels, apiHost, log } = config;
  const claudeAuthMode = config.claudeAuthMode ?? 'subscription';
  // ENG-7152: OpenRouter BYO-model mode routes this agent's INFERENCE through
  // OpenRouter via the ANTHROPIC_BASE_URL/ANTHROPIC_AUTH_TOKEN override below,
  // and never injects ANTHROPIC_API_KEY. ENG-7213: unlike api_key mode it does
  // NOT purge the claude.ai OAuth creds; they are retained so the claude.ai-
  // gated channels feature (Telegram/Slack/direct-chat) can authenticate while
  // inference still bills OpenRouter. The two coexist - inference follows the
  // base-URL override, channels follow the OAuth session (see the credential
  // branch below). "auth mode" is the INFERENCE source; channel OAuth is
  // orthogonal and synced whenever present.
  const openRouterMode = !!config.openRouter;
  const tmuxSession = `agt-${codeName}`;

  log(
    `[persistent-session] Starting tmux session '${tmuxSession}' for '${codeName}' (auth=${openRouterMode ? 'openrouter' : claudeAuthMode})`,
  );

  try {
    sanitizeMcpJson(mcpConfigPath, apiHost);

    // Kill any existing tmux session (clean slate)
    try {
      execSync(`tmux kill-session -t ${tmuxSession} 2>/dev/null`, { stdio: 'ignore' });
    } catch { /* no existing session */ }

    // When running as root, claude looks at $HOME/.claude/.credentials.json
    // Credential branch. The discriminator is the INFERENCE source; channel
    // OAuth is orthogonal (ENG-7213):
    //
    //   subscription:  sync OAuth creds into /root/.claude - used for BOTH
    //                  inference and channels. Do NOT set ANTHROPIC_API_KEY.
    //   openrouter:    sync OAuth creds into /root/.claude too, but they are
    //                  used ONLY to unlock the claude.ai-gated channels feature;
    //                  inference is routed to OpenRouter by the
    //                  ANTHROPIC_BASE_URL/ANTHROPIC_AUTH_TOKEN override set below.
    //                  The base-URL override is authoritative for inference, so
    //                  claude never falls back to the OAuth session for model
    //                  calls (verified live, ENG-7213: a dummy OpenRouter token
    //                  errors rather than the subscription silently answering).
    //   api_key:       DELETE any /root/.claude creds so claude can't fall back
    //                  to a stale OAuth session, then inject ANTHROPIC_API_KEY
    //                  into the spawn env below. Leaving both present is the
    //                  "confused deputy" path: claude's ANTHROPIC_API_KEY-vs-OAuth
    //                  precedence has changed between versions and is undocumented.
    //                  (OpenRouter mode is exempt - it uses ANTHROPIC_AUTH_TOKEN +
    //                  a base-URL override, not ANTHROPIC_API_KEY, so there is no
    //                  api-key/OAuth ambiguity to resolve.)
    if (resolveOAuthCredAction(claudeAuthMode, openRouterMode) === 'sync') {
      const credsSynced = syncClaudeCredsToRoot();
      const onLinuxRoot = platform() === 'linux' && typeof process.getuid === 'function' && process.getuid() === 0;
      if (openRouterMode) {
        if (credsSynced) {
          log(`[persistent-session] OpenRouter mode for '${codeName}' - model=${config.openRouter!.model}; inference via OpenRouter, claude.ai OAuth retained for channels.`);
        } else if (onLinuxRoot) {
          // Inference still works; only the claude.ai-gated channels stay dark
          // until the host is paired. Non-fatal by design.
          log(`[persistent-session] OpenRouter mode for '${codeName}' - model=${config.openRouter!.model}; no claude.ai OAuth creds under /root/.claude or /home/*, so channels (Telegram/Slack/direct-chat) will not load. Inference still works via OpenRouter. Run 'claude /login' on the host to enable channels.`);
        }
      } else if (!credsSynced && onLinuxRoot) {
        log(`[persistent-session] No Claude Code credentials found under /root/.claude or /home/*. Pair via browser from the host page, or run 'claude /login' on the host.`);
      }
    } else {
      // api_key mode — purge subscription creds under the current user's
      // home. Previously this was hardcoded to /root/.claude, which missed
      // non-root runs and macOS dev setups — letting OAuth creds silently
      // override the api_key in those environments. homedir() is what
      // claude-code itself reads, so that's the directory to clear.
      const claudeDir = join(homedir(), '.claude');
      for (const filename of ['.credentials.json', 'credentials.json']) {
        const p = join(claudeDir, filename);
        if (existsSync(p)) {
          try {
            rmSync(p, { force: true });
            log(`[persistent-session] Removed ${p} (api_key mode active — preventing OAuth fallback)`);
          } catch { /* non-fatal */ }
        }
      }
      if (!config.anthropicApiKey) {
        log(`[persistent-session] api_key mode but no anthropicApiKey passed. Session will fail auth.`);
      }
    }

    // Build claude args
    const args: string[] = [];

    // ENG-6039: resume today's session when its transcript exists,
    // cold-start otherwise — see resolveSessionSpawnDecision for the
    // full decision table and the ENG-5397 history. The resume-mode
    // dialog claude may show on --resume is auto-dismissed by
    // acceptDialogs (ENG-5364).
    const decision = resolveSessionSpawnDecision({
      codeName,
      projectDir,
      agentTimezone: config.agentTimezone ?? undefined,
    });
    const sessionId = decision.sessionId;
    const resuming = decision.flag === '--resume';
    args.push(decision.flag, sessionId);
    log(
      `[persistent-session] ${resuming ? 'Resuming' : 'Starting'} session ${sessionId} for '${codeName}' (${decision.reason})`,
    );

    // ENG-5431: advance the daily-session marker to today's date with the
    // UUID we just resolved. The day-rollover detector in manager-worker.ts
    // reads `current.date` to decide whether to restart this session at
    // the day boundary; if we don't update it on every spawn, that check
    // keeps firing every supervisor tick. On the ENG-6039 resume/fresh
    // paths this is an idempotent no-op (getOrCreateDailySession /
    // rotateDailySession just wrote the same entry); it does real work
    // only on the AGT_DISABLE_SESSION_RESUME path, whose randomUUID()
    // bypasses those helpers. Writing here keeps the marker in lockstep
    // with the running session, so `isStaleForToday` flips to false on
    // the next tick instead of looping.
    //
    // The write is sync disk IO (atomic tmp+rename inside writeFile()).
    // Isolate it so an ENOSPC / EACCES doesn't take down the spawn flow
    // — the marker is bookkeeping, not on the critical path. Worst case
    // if it fails: next supervisor tick re-fires the day-rollover restart
    // (the bug this fix exists for), but that's strictly no worse than
    // pre-fix behaviour and self-heals the next time the write succeeds.
    try {
      markDailySessionSpawn(codeName, sessionId, new Date(), config.agentTimezone ?? undefined);
    } catch (err) {
      log(
        `[persistent-session] Failed to update daily-session marker for '${codeName}': ${(err as Error).message}`,
      );
    }

    // ENG-5927 (PR-4): publish freshness for the in-session direct-chat MCP so
    // its doorbell replay is resume-aware — a fresh (--session-id) session may
    // drain the pending backlog; a resumed (--resume) one must not re-deliver
    // pre-restart messages into a transcript that already handled them. Same
    // best-effort, off-critical-path posture as the daily-session marker above.
    try {
      writeDirectChatSessionState(config.agentId, {
        fresh: !resuming,
        sessionId,
        startedAtMs: Date.now(),
        // ENG-7263: snapshot the MCP servers this session loads at spawn (the
        // sanitized --mcp-config it launches with), so the session-tool-bind
        // probe can confirm "the running session loaded server X" by membership
        // rather than the churning .mcp.json file mtime.
        mcpServerKeys: collectMcpServerNames(mcpConfigPath),
      });
    } catch (err) {
      log(
        `[persistent-session] Failed to write direct-chat session state for '${codeName}': ${(err as Error).message}`,
      );
    }

    if (channels.length > 0) args.push('--channels', ...channels);
    if (devChannels.length > 0) args.push('--dangerously-load-development-channels', ...devChannels);
    args.push('--mcp-config', mcpConfigPath);
    if (existsSync(claudeMdPath)) args.push('--system-prompt-file', claudeMdPath);
    // ENG-5631: pass the agent's model as a session-scoped --model alias.
    // This is the only mechanism that actually takes effect for subscription
    // agents — the model written into the per-agent settings.json isn't read
    // by Claude Code, and a non-interactive agent never runs `/model`. Omit
    // the flag for an empty/unknown model so Claude Code uses the tier default.
    // ENG-7152: in OpenRouter mode the model is the bare provider/model id
    // delivered via ANTHROPIC_MODEL (below), NOT a Claude family alias — so skip
    // the `--model <alias>` path entirely (claudeModelAlias would return null for
    // a non-Claude id anyway, but an `openrouter/anthropic/…` id would wrongly
    // resolve to a subscription alias and fight the base-URL override).
    if (!openRouterMode) {
      const modelAlias = claudeModelAlias(config.primaryModel);
      if (modelAlias) args.push('--model', modelAlias);
    }
    args.push('--allow-dangerously-skip-permissions');
    args.push('--dangerously-skip-permissions');
    args.push('--strict-mcp-config');
    args.push('--name', tmuxSession);

    // Restrict tools to only the agent's configured MCP servers + built-in tools.
    // Without this, agents inherit the user's personal MCPs (Gmail, Calendar, etc.)
    const mcpServerNames = collectMcpServerNames(mcpConfigPath);
    args.push('--allowedTools', buildAllowedTools(mcpServerNames));

    // NOTE: CLAUDE_CODE_SIMPLE=1 blocks account plugins BUT also breaks
    // channel auth (Slack/Telegram require claude.ai OAuth). Instead, rely on
    // --strict-mcp-config + --allowedTools for tool isolation. Account plugins
    // may appear in the tool list but --allowedTools prevents calling them.
    //
    // IS_SANDBOX=1 bypasses claude's refusal to run under root/sudo with
    // --dangerously-skip-permissions. Dedicated EC2 hosts running only
    // agent workloads are effectively sandboxed (org-scoped VPC, no inbound,
    // no other tenants). Without this, the tmux session exits immediately
    // with "cannot be used with root/sudo privileges for security reasons".
    //
    // ENG-4717: previously we read `.env.integrations` and inlined every
    // KEY=VALUE pair onto the bash command string we handed tmux. That
    // string is the long-running shell process's argv — anything in it is
    // visible via `ps -eo command` for the entire session lifetime, which
    // means tokens like XURL_API_KEY and GRANOLA_ACCESS_TOKEN leaked to
    // any user who could ps on the host. We now write a wrapper script
    // (mode 0700) that sources the env file inside the spawned shell and
    // exec's claude — same pattern as the ACP wrapper below. The argv
    // visible to ps is just `bash <wrapper>`; the secrets never cross the
    // command-line boundary.
    // Fresh sessions get the boot prompt; the SessionStart orient hook
    // (matcher `startup`) fires before this turn lands and injects the
    // agent's orientation context (today's memory, open kanban, pending
    // channel threads) — by the time the agent responds "Ready." it
    // already knows where it left off.
    //
    // Resumed sessions (ENG-6039) get an empty init prompt: the
    // conversation already has its context, the orient hook doesn't
    // match source=resume, and ENG-5353 taught us that appending even a
    // blank positional turn to a resumed transcript can trip upstream
    // request-shape regressions. writePersistentClaudeWrapper omits the
    // positional entirely when initPrompt is ''.
    const initPrompt = resuming
      ? ''
      : 'You are now online. Say "Ready." and wait for incoming messages. Do not run any tools or load any data until a message arrives.';
    const claudeBin = resolveClaudeBinary();
    const claudeArgsJoined = args
      .map(a => (a.includes(' ') || a.includes('*')) ? JSON.stringify(a) : a)
      .join(' ');

    const wrapperPath = writePersistentClaudeWrapper({
      projectDir,
      claudeBin,
      initPrompt,
      claudeArgsJoined,
    });

    // ANTHROPIC_API_KEY is passed via `tmux new-session -e` so it lands in
    // the session shell's env without ever appearing in the claude shell's
    // argv — `ps aux` on the long-running `bash -c "claude ..."` process
    // would otherwise expose the raw key for the session's lifetime.
    // The `-e` flag's exposure is bounded to the new-session invocation,
    // which exits in well under a second.
    const tmuxSessionEnvArgs: string[] = [];
    if (openRouterMode) {
      // ENG-7152: point Claude Code at OpenRouter's native Anthropic endpoint.
      // Same `-e KEY=VALUE` posture as ANTHROPIC_API_KEY — the secret lands in
      // the session shell env, never on the claude argv. ANTHROPIC_SMALL_FAST_MODEL
      // MUST also be an OpenRouter id, else the background/fast-model calls hit a
      // Claude id the custom base URL can't serve (spike gotcha, ENG-7148).
      const or = config.openRouter!;
      tmuxSessionEnvArgs.push('-e', `ANTHROPIC_BASE_URL=${OPENROUTER_ANTHROPIC_BASE_URL}`);
      tmuxSessionEnvArgs.push('-e', `ANTHROPIC_AUTH_TOKEN=${or.authToken}`);
      tmuxSessionEnvArgs.push('-e', `ANTHROPIC_MODEL=${or.model}`);
      if (or.smallFastModel) {
        tmuxSessionEnvArgs.push('-e', `ANTHROPIC_SMALL_FAST_MODEL=${or.smallFastModel}`);
      }
    } else if (claudeAuthMode === 'api_key' && config.anthropicApiKey) {
      tmuxSessionEnvArgs.push('-e', `ANTHROPIC_API_KEY=${config.anthropicApiKey}`);
    }

    // The command tmux runs is just the wrapper path — no secrets, no
    // long token strings, nothing for ps to expose. Under Docker isolation
    // (ADR-0014) tmux instead exec's a `docker run` that runs the same
    // wrapper inside the agent's sandbox; secrets still never cross the argv
    // (the wrapper sources .env.integrations from the mounted tree, and the
    // API key forwards by name only).
    const sessionHomeDir = process.env.HOME?.trim() || homedir();
    // ENG-6579: when egress is on, materialise the per-agent allowlist to a file
    // the squid sidecar bind-mounts. SECURITY: the list comes from the manager
    // (trusted control-plane data, NOT the agent's on-disk TOOLS.md), and the
    // file is written under `~/.augmented/_egress/` - a host-only dir the agent
    // never mounts (cf. the agent's mounts in buildDockerRunCommand). So neither
    // the allowlist's source nor its on-disk form is agent-writable; a
    // compromised agent cannot widen its own egress (CodeRabbit, PR #1528).
    let egress: { allowlistHostPath: string } | undefined;
    if (egressMode(codeName) === 'allowlist') {
      const allowlist = config.egressAllowlist ?? buildEgressAllowlist(null);
      const allowlistHostPath = writeEgressAllowlist(codeName, allowlist, sessionHomeDir);
      egress = { allowlistHostPath };
      log(`[persistent-session] egress allowlist for '${codeName}': ${allowlist.length} domains (deny-by-default)`);
    }
    // ENG-6670: npx-based MCPs (cloud-broker) get a per-agent writable npm cache
    // via npm_config_cache inside buildDockerRunCommand (the agent's own rw tree)
    // - no host ~/.npm mount needed, and per-agent keeps the T5 boundary.
    const claudeCmd = isolationMode(codeName) === 'docker'
      ? buildDockerRunCommand({
          codeName,
          agentId: config.agentId,
          wrapperPath,
          projectDir,
          homeDir: sessionHomeDir,
          runId: config.runId ?? undefined,
          passApiKey: !openRouterMode && claudeAuthMode === 'api_key' && !!config.anthropicApiKey,
          // ENG-7152: forward the OpenRouter ANTHROPIC_* vars (by name) from the
          // session-shell env into the container, same posture as passApiKey.
          passOpenRouter: openRouterMode,
          egress,
        })
      : JSON.stringify(wrapperPath);

    // ENG-4632: defensively backfill HOME/USER before tmux spawns its
    // shell. When the manager is launched via `aws ssm send-command`
    // (or any non-login init), process.env can lack HOME — tmux
    // inherits that, the agent's claude process can't find
    // ~/.claude/.credentials.json, and falls back to the interactive
    // login picker forever. The managerStartCommand also applies this,
    // but a missing HOME at the persistent-session boundary is a
    // belt-and-braces fail-closed point worth keeping.
    const tmuxEnv: NodeJS.ProcessEnv = {
      ...process.env,
      // Treat empty-string as missing too — `HOME=""` makes ~ resolve
      // to cwd, which is the same broken outcome as no HOME, just
      // better hidden.
      HOME: (process.env.HOME?.trim()) || homedir(),
      USER: (process.env.USER?.trim()) || userInfo().username,
    };
    // ENG-5051: stamp AGT_RUN_ID so Claude Code's `${AGT_RUN_ID}`
    // substitution in .mcp.json resolves to a real run for child MCPs
    // (cloud-broker, etc). Without this the persistent session boots
    // with the literal placeholder leaking through and cloud-broker
    // fails its startup guard. Only set when manager-worker successfully
    // minted a run — empty/missing keeps the legacy behaviour
    // (placeholder leaks, agent must mint by hand).
    if (config.runId) {
      tmuxEnv['AGT_RUN_ID'] = config.runId;
    }

    // ENG-5901 (CodeRabbit #1731): fail-fast probe — one structured line
    // per `${VAR}` in the rendered .mcp.json that the *actual* spawn env
    // leaves unset or empty. Based on the real tmuxEnv (HOME/USER
    // backfill, AGT_RUN_ID stamp) plus the `-e`-injected
    // ANTHROPIC_API_KEY, overlaid with .env.integrations exactly as the
    // wrapper's `source` will do — not raw process.env, which would
    // false-positive on injected keys. Observational only; the silent
    // failure it catches is "substitutes to empty → MCP boots → upstream
    // 401s → channel dies".
    //
    // ENG-6670: for an ISOLATED (docker) agent the in-container env is NOT
    // tmuxEnv — claude only sees the vars buildDockerRunCommand `-e`-forwards
    // (plus .env.integrations, which the wrapper sources inside the container).
    // Probing against tmuxEnv would mask exactly the gap that bit slack/telegram
    // (AGT_API_KEY/AGT_HOST present in the manager env but not forwarded →
    // /host/exchange 401). So mirror the forward set here. KEEP IN SYNC with the
    // envArgs.push forwards in buildDockerRunCommand.
    const apiKeyEnv =
      !openRouterMode && claudeAuthMode === 'api_key' && config.anthropicApiKey
        ? { ANTHROPIC_API_KEY: config.anthropicApiKey }
        : {};
    // ENG-7152: mirror the openRouterMode `-e` injections so the probe sees the
    // real spawn env (else a `${ANTHROPIC_MODEL}`-style ref would false-positive).
    const openRouterEnv = openRouterMode
      ? {
          ANTHROPIC_BASE_URL: OPENROUTER_ANTHROPIC_BASE_URL,
          ANTHROPIC_AUTH_TOKEN: config.openRouter!.authToken,
          ANTHROPIC_MODEL: config.openRouter!.model,
          ...(config.openRouter!.smallFastModel
            ? { ANTHROPIC_SMALL_FAST_MODEL: config.openRouter!.smallFastModel }
            : {}),
        }
      : {};
    const probeBaseEnv =
      isolationMode(codeName) === 'docker'
        ? {
            HOME: tmuxEnv['HOME'],
            ...apiKeyEnv,
            ...openRouterEnv,
            ...(config.runId ? { AGT_RUN_ID: config.runId } : {}),
            AGT_API_KEY: tmuxEnv['AGT_API_KEY'],
            AGT_HOST: tmuxEnv['AGT_HOST'],
            AGT_AGENT_ID: config.agentId,
          }
        : { ...tmuxEnv, ...apiKeyEnv, ...openRouterEnv };
    for (const f of probeMcpEnvSubstitution({
      mcpConfigPath,
      envIntegrationsPath: join(projectDir, '.env.integrations'),
      baseEnv: probeBaseEnv,
    })) {
      log(`[persistent-session] ${formatMissingVar(f)} agent=${codeName}`);
    }

    // Start tmux session with claude in it
    const child = spawn('tmux', [
      'new-session', '-d', '-s', tmuxSession, '-c', projectDir,
      ...tmuxSessionEnvArgs, claudeCmd,
    ], {
      cwd: projectDir,
      stdio: ['ignore', 'pipe', 'pipe'],
      env: tmuxEnv,
    });

    child.on('close', (code) => {
      if (code !== 0) {
        log(`[persistent-session] Failed to create tmux session for '${codeName}' (exit ${code})`);
        session.status = 'crashed';
        session.startedAt = Date.now();
        session.restartCount++;
        return;
      }
      log(`[persistent-session] tmux session '${tmuxSession}' created for '${codeName}'`);

      // ENG-4659: redirect pane output to a log file so we can recover
      // claude's actual error message after the session dies (claude's
      // stderr is otherwise unreachable since the tmux child is detached
      // before claude even prints).
      setupPaneLog(tmuxSession, codeName, log);

      // Track which session UUID we just spawned with so the recovery
      // hook can detect "same UUID failing repeatedly" and rotate.
      // Note: currentSessionId (set on spawn) is distinct from
      // lastFailureSessionId (set on the *previous* failure). Comparing
      // them is what makes the rotation gate work.
      session.currentSessionId = sessionId;

      // Auto-accept startup dialogs. Kanban work is driven by the
      // manager-gated hybrid edge-trigger injection (ENG-5435, ENG-5662),
      // not an in-session /loop, so there's nothing to arm here.
      acceptDialogs(tmuxSession, codeName, log, config.primaryModel ?? null, sessionId).catch(() => {});
    });

    child.on('error', (err) => {
      log(`[persistent-session] Failed to start tmux for '${codeName}': ${err.message}`);
      session.status = 'crashed';
      session.startedAt = Date.now();
      session.restartCount++;
    });

    session.startedAt = Date.now();
    session.status = 'running';
    session.restartCount = 0;
  } catch (err) {
    log(`[persistent-session] Failed to start session for '${codeName}': ${(err as Error).message}`);
    session.status = 'crashed';
    session.startedAt = Date.now();
    session.restartCount++;
  }
}

// ENG-6017: dialog detection + dismissal moved to claude-dialogs.ts so the
// channel-input-watchdog can share it without an import cycle. The detectors
// are re-exported through `_internals` below to keep existing tests green.

/**
 * Detect whether the session has actually spawned its MCP server
 * children — the only reliable signal that claude reached the running
 * REPL. tmux pane content alone can't distinguish "Ready" prompts
 * from a stuck splash screen, so we shell out to ps and look for
 * children of the claude process.
 *
 * ENG-4634: previously the helper logged 'Session ready' whenever the
 * pane had a `❯` not preceded by 'Enter to confirm' — but the login
 * picker also has a `❯` and would short-circuit out as ready. Verify
 * a real MCP child exists (slack-channel.js / direct-chat-channel.js
 * / etc.) before claiming success.
 */
function hasMcpChildren(tmuxSession: string): boolean {
  try {
    // Find the claude process inside this tmux session by --name flag
    // (set when the manager launches claude — see spawnSession).
    const claudePidOut = execSync(
      `pgrep -f -- "--name ${tmuxSession}" 2>/dev/null || true`,
      { encoding: 'utf-8' },
    ).trim();
    if (!claudePidOut) return false;
    // pgrep can match multiple processes (the bash shell wrapping
    // claude, plus claude itself). We want the **claude** process —
    // its children are the MCP servers we're checking for. Process
    // ordering means the wrapper shell is the LOWER PID and claude
    // (forked after the shell parses its args) is HIGHER. Pick the
    // max so `pgrep -P` finds the MCP children, not the bash kids.
    const pids = claudePidOut.split('\n').map((p) => Number(p)).filter((p) => p > 0);
    if (pids.length === 0) return false;
    const claudePid = Math.max(...pids);
    // List child processes; if any look like an MCP channel server,
    // we're in business.
    const childrenOut = execSync(
      `pgrep -P ${claudePid} 2>/dev/null || true`,
      { encoding: 'utf-8' },
    ).trim();
    if (!childrenOut) return false;
    const childPids = childrenOut.split('\n').map((p) => p.trim()).filter(Boolean);
    for (const cp of childPids) {
      const cmdline = execSync(
        `cat /proc/${cp}/cmdline 2>/dev/null | tr '\\0' ' ' || ps -p ${cp} -o args= 2>/dev/null || true`,
        { encoding: 'utf-8' },
      );
      if (
        /slack-channel\.js|telegram-channel\.js|direct-chat-channel\.js|composio_/i.test(cmdline)
      ) {
        return true;
      }
    }
    return false;
  } catch {
    return false;
  }
}

async function acceptDialogs(
  tmuxSession: string,
  codeName: string,
  log: (msg: string) => void,
  primaryModel: string | null = null,
  sessionId: string | null = null,
): Promise<void> {
  // Track whether we've already surfaced the login-picker warning so
  // operators don't get one log line per polling iteration. The
  // picker won't dismiss itself — once we've reported it, just keep
  // probing for the eventual recovery (e.g. operator completes OAuth
  // out-of-band) without re-spamming the log.
  let loginPickerReported = false;

  // Login-picker iterations don't count against the dialog-dismissal
  // budget — the operator can take minutes to complete OAuth via the
  // Hosts page, and we want acceptDialogs to still be running to
  // dismiss the trust + bypass dialogs that follow. Track the two
  // kinds of iterations separately so a slow OAuth doesn't burn the
  // 30s budget meant for the post-pair dialog cascade. Cap login-
  // picker waits at 15 minutes total to avoid leaking a forever-
  // polling helper if the operator walks away.
  let dialogIterations = 0;
  const MAX_DIALOG_ITERATIONS = 15;
  let loginPickerIterations = 0;
  const MAX_LOGIN_PICKER_ITERATIONS = 450; // 450 * 2s = 15 min

  while (
    dialogIterations < MAX_DIALOG_ITERATIONS &&
    loginPickerIterations < MAX_LOGIN_PICKER_ITERATIONS
  ) {
    await new Promise((r) => setTimeout(r, 2000));
    try {
      const screen = execSync(`tmux capture-pane -t ${tmuxSession} -p 2>/dev/null`, { encoding: 'utf-8' });

      // ENG-4634: handle the login picker BEFORE any other dialog
      // pattern. The picker has a `❯` cursor that the generic exit
      // branch at the bottom of this loop would otherwise read as
      // "Session ready". Press no key — sending Enter would trigger
      // an OAuth flow that requires browser interaction the agent
      // can't complete. Surface a clear, parseable log line so
      // operators / monitoring can route the operator to the
      // Hosts page to complete pairing.
      if (isLoginPickerVisible(screen)) {
        if (!loginPickerReported) {
          log(`[persistent-session] CLAUDE LOGIN REQUIRED for '${codeName}' — agent cannot start until ~/.claude.json is provisioned. Pair via the Hosts page or run 'claude /login' on the host.`);
          loginPickerReported = true;
        }
        loginPickerIterations++;
        continue;
      }

      // Reached the dialog cascade — count this iteration against the
      // shorter budget.
      dialogIterations++;

      // ENG-6017: the dialog cascade (theme picker → trust → resume-mode →
      // dev channels → MCP confirm → bypass permissions → session feedback)
      // now lives in sweepDialogs() so the channel-input-watchdog and the
      // inject-time hygiene share the exact same recognition. Behaviour and
      // log wording are unchanged (the theme-picker branch still runs before
      // the generic `❯ no Enter to confirm` exit below, since picker rows
      // also render with `❯`).
      const dialogAction = sweepDialogs(screen);
      if (dialogAction) {
        await sendDialogKeys(tmuxSession, dialogAction);
        log(`[persistent-session] ${dialogAction.logMessage} for '${codeName}'`);
        continue;
      }
      if (screen.includes('❯') && !screen.includes('Enter to confirm')) {
        // ENG-4634: don't trust the pane alone. Verify at least one
        // MCP server child has actually been spawned by the claude
        // process before declaring the session ready — otherwise a
        // splash-screen-with-cursor false-positive can race the
        // login picker and leave the agent silently broken.
        if (hasMcpChildren(tmuxSession)) {
          log(`[persistent-session] Session ready for '${codeName}' — MCP servers spawned`);
          // ENG-5770: opt this session into Anthropic's fast-output mode when
          // the agent's primary_model carries the `[fast]` marker. The send
          // happens before any inbound message is injected so the first real
          // prompt of the session is already running in fast mode. Banner is
          // re-checked here to skip + warn if a silent downgrade landed the
          // session on Sonnet/Haiku instead of Opus.
          await maybeSendFastMode({
            tmuxSession,
            codeName,
            primaryModel,
            sessionId,
            screen,
            log,
          });
          break;
        }
        // Pane looks idle but no MCP children yet — claude may still
        // be initialising. Keep polling; the loop bound caps total
        // wait at 30s.
      }
    } catch { break; }
  }
}

// ---------------------------------------------------------------------------
// ENG-5770: fast-mode `/fast` send. Called once per ready-banner detection
// (boot + every respawn path that re-runs acceptDialogs). Skips when the
// agent isn't on a `[fast]` variant or when the live banner shows a non-Opus
// family — `/fast` is currently only valid on Opus 4.6/4.7 and a silent
// model downgrade is the failure mode we want to surface, not paper over.
// ---------------------------------------------------------------------------

interface FastModeContext {
  tmuxSession: string;
  codeName: string;
  primaryModel: string | null;
  sessionId: string | null;
  /** Pane capture that triggered the ready-detection; used to inspect the model banner. */
  screen: string;
  log: (msg: string) => void;
}

/**
 * Inspect the live tmux pane to decide whether `/fast` is safe to send and,
 * if so, deliver it. The decision is intentionally conservative — we only
 * send when the screen mentions `opus`, never when it shows another family,
 * and never when the agent isn't tagged `[fast]`. Failures are logged and
 * swallowed: a missing `/fast` is non-fatal and we don't want a bad capture
 * to crash the spawn path.
 */
async function maybeSendFastMode(ctx: FastModeContext): Promise<void> {
  if (!isClaudeFastMode(ctx.primaryModel)) return;

  const sid = ctx.sessionId ?? 'unknown';
  const banner = ctx.screen.toLowerCase();
  const hasOpus = banner.includes('opus');
  const hasNonOpus = banner.includes('sonnet') || banner.includes('haiku');

  // A pane that mentions a non-Opus family without also mentioning Opus is
  // the silent-downgrade case. Skip + warn so the operator can see why their
  // fast-mode selection didn't take effect.
  if (hasNonOpus && !hasOpus) {
    ctx.log(
      `[fast-mode] skip /fast for agent=${ctx.codeName} session=${sid} — banner shows non-Opus model`,
    );
    return;
  }

  // Banner doesn't name a family at all (unusual layout, partial capture).
  // Skip rather than guess; a missing /fast just costs the operator the
  // fast-mode speed-up on this respawn.
  if (!hasOpus) {
    ctx.log(
      `[fast-mode] skip /fast for agent=${ctx.codeName} session=${sid} — Opus not visible in banner`,
    );
    return;
  }

  const ok = sendToAgent(ctx.tmuxSession, '/fast');
  if (ok) {
    ctx.log(`[fast-mode] sent /fast for agent=${ctx.codeName} session=${sid}`);
  } else {
    ctx.log(`[fast-mode] failed to send /fast for agent=${ctx.codeName} session=${sid} — tmux send-keys errored`);
  }
}

// ---------------------------------------------------------------------------
// Prompt-ready detection + tmux send (shared by the manager-gated hybrid
// kanban-work inject, ENG-5435). The in-session `/loop kanban-work` arm
// (ENG-5404) that previously lived here was removed in ENG-5662 — kanban work
// is now driven solely by the hybrid edge-trigger.
// ---------------------------------------------------------------------------

/**
 * Wait until the Claude Code REPL prompt is ready to accept input.
 *
 * v1 is intentionally simple: poll the pane every 500ms for the `❯ ` prompt
 * marker, return true the first time we see it, return false on timeout.
 * `acceptDialogs()` is the upstream gate that handles dialogs/login pickers,
 * so by the time this runs the pane is either at the prompt or fully wedged.
 *
 * The 10s cap is well past the typical post-acceptDialogs settle time
 * (~1-2s in observation) and short enough that a bad spawn fails fast
 * instead of holding up the manager loop.
 */
async function waitForPromptReady(tmuxSession: string): Promise<boolean> {
  const deadline = Date.now() + 10_000;
  while (Date.now() < deadline) {
    try {
      // CodeRabbit PR #1275: execFileSync (not execSync) so tmuxSession
      // is passed as an argv entry rather than interpolated into a
      // shell string. Matches the safer pattern used in acceptDialogs
      // for ENG-5364's resume-mode dismiss path.
      const screen = execFileSync(
        'tmux',
        ['capture-pane', '-t', tmuxSession, '-p'],
        { encoding: 'utf-8', stdio: ['ignore', 'pipe', 'ignore'] },
      );
      if (screen.includes('❯ ')) return true;
    } catch { /* session may not exist yet — retry */ }
    await new Promise((r) => setTimeout(r, 500));
  }
  return false;
}

/**
 * Type of the function used to deliver a command string to the agent's
 * tmux session (via `sendToAgent`). Exposed via the _internals.__setArmSender
 * test seam so unit tests can swap in a spy without touching tmux. The
 * sender returns true on success, false on any tmux/exec failure.
 */
type ArmSender = (tmuxSession: string, command: string) => boolean;

// ENG-5793: delay between the text-send and the Enter-send in
// defaultArmSender. Exposed as a const so future tuning has a single seam.
export const SEND_KEYS_ENTER_DELAY_MS = 50;

/**
 * Synchronous millisecond sleep. defaultArmSender is itself synchronous
 * (returns a boolean, not a Promise) and its call sites assume that;
 * Atomics.wait on a transient SharedArrayBuffer is the standard Node
 * idiom for a blocking sleep without changing the function signature.
 * The delay is tiny (50ms) so the cost vs an event-loop sleep is
 * negligible in practice.
 */
function sleepBlockingMs(ms: number): void {
  const view = new Int32Array(new SharedArrayBuffer(4));
  Atomics.wait(view, 0, 0, ms);
}

function defaultArmSender(tmuxSession: string, command: string): boolean {
  try {
    // ENG-5793: split the text and the Enter into two separate
    // `tmux send-keys` invocations.
    //
    // The previous shape was `send-keys -t <s> <text> Enter` in ONE call.
    // When tmux's `extended-keys-format` is `csi-u` (the default on
    // tmux 3.4+ and what AWS EC2 hosts inherit), tmux delivers the
    // whole call as a bracketed paste (the text is multi-byte) and
    // re-encodes the trailing CR (0x0D) inside the paste as a CSI-u
    // sequence (`ESC[13;1u`). Claude Code's bracketed-paste tokenizer
    // doesn't decode CSI-u sequences within paste brackets, so the
    // encoded carriage return is silently dropped. The text lands in
    // the input buffer perfectly readable, the cursor sits at `❯`,
    // the agent never sees a turn. See anthropics/claude-code#43169.
    //
    // Split-call shape:
    //   1. `send-keys -l <text>` — `-l` (literal) sends the text as raw
    //      bytes without interpreting key names. tmux still brackets it
    //      as a paste, but the paste-end marker fires when this call
    //      returns, so the Enter that follows is OUTSIDE the paste.
    //   2. Short sleep — give Claude Code's TUI a tick to commit the
    //      paste before the Enter arrives. Without this, the two
    //      invocations can be batched into one input window on the
    //      app's side, putting us back in the same paste-internal-CR
    //      trap.
    //   3. `send-keys Enter` — sent as a standalone keystroke, NOT
    //      inside a paste, so the `\r` is delivered raw and Claude
    //      Code treats it as "submit current input" as the Kitty
    //      protocol spec mandates for the legacy Enter encoding.
    //
    // The old comment here claimed the two-step shape was needed so
    // Claude Code's REPL would parse `/` as a slash command — that
    // turned out to be incidental. Single-call `send-keys text Enter`
    // worked for slash commands only because they're short enough that
    // some tmux/Claude Code versions short-circuited the paste-wrap.
    // The csi-u-vs-paste-tokenizer interaction is the real reason
    // plain-text injects silently fail in the same call shape.
    execFileSync('tmux', ['send-keys', '-t', tmuxSession, '-l', command], {
      stdio: ['ignore', 'ignore', 'pipe'],
    });
    sleepBlockingMs(SEND_KEYS_ENTER_DELAY_MS);
    execFileSync('tmux', ['send-keys', '-t', tmuxSession, 'Enter'], {
      stdio: ['ignore', 'ignore', 'pipe'],
    });
    return true;
  } catch {
    return false;
  }
}

let armSender: ArmSender = defaultArmSender;

// ---------------------------------------------------------------------------
// ENG-6017: inject-time pane hygiene (dialog sweep + orphan-input clear)
// ---------------------------------------------------------------------------

/**
 * Pane capture used by the pre-send hygiene. Returns null when the session
 * (or tmux itself) is unavailable — callers treat null as "skip hygiene",
 * which keeps unit tests and non-tmux environments fast and silent.
 * Swappable via _internals.__setPaneCapture for tests.
 */
type PaneCapture = (tmuxSession: string) => string | null;

function defaultPaneCapture(tmuxSession: string): string | null {
  try {
    return execFileSync('tmux', ['capture-pane', '-t', tmuxSession, '-p'], {
      encoding: 'utf-8',
      stdio: ['ignore', 'pipe', 'ignore'],
      timeout: 2_000,
    });
  } catch {
    return null;
  }
}

let paneCapture: PaneCapture = defaultPaneCapture;

/**
 * Key sender used by the pre-send hygiene (dialog dismissal + C-u clear).
 * One send-keys call per key — never batched, so multi-key sequences can't
 * be wrapped into a single bracketed paste. Swappable via
 * _internals.__setHygieneKeySender for tests.
 */
type HygieneKeySender = (
  tmuxSession: string,
  keys: readonly string[],
  interKeyDelayMs: number,
) => Promise<void>;

const defaultHygieneKeySender: HygieneKeySender = async (tmuxSession, keys, interKeyDelayMs) => {
  for (let i = 0; i < keys.length; i++) {
    if (i > 0 && interKeyDelayMs > 0) {
      await new Promise((r) => setTimeout(r, interKeyDelayMs));
    }
    execFileSync('tmux', ['send-keys', '-t', tmuxSession, keys[i]!], {
      stdio: 'ignore',
    });
  }
};

let hygieneKeySender: HygieneKeySender = defaultHygieneKeySender;

/**
 * ENG-6017: pre-send pane hygiene for the tmux send-keys fallback.
 *
 * Two failure modes observed live (koda, 2026-06-04) that make a blind
 * send-keys land wrong:
 *
 *  1. A dialog (e.g. Claude Code's session-feedback rating prompt)
 *     overlays the REPL — the typed text and Enter go into the dialog,
 *     not the input box.
 *  2. A previous injection's text is still sitting unsubmitted in the
 *     input box — the new text would concatenate onto it, corrupting
 *     both messages.
 *
 * So: capture the pane once; dismiss a recognised dialog if present
 * (default-deny — unknown overlays are logged, never keyed); then if the
 * input box still holds orphaned text, clear it with C-u (NEVER submit
 * it — the operator may have already re-sent a corrected version, and
 * blind-submitting a stale ghost instruction is worse than dropping it;
 * the content is logged hash-only per the prod logging policy, and the
 * channel-input-watchdog had its bounded chances to submit it first).
 *
 * Best-effort by design: any capture/send failure skips hygiene and lets
 * the send proceed — this layer must never block delivery.
 */
async function preSendPaneHygiene(
  tmuxSession: string,
  codeName: string,
  log: (msg: string) => void,
): Promise<void> {
  try {
    let screen = paneCapture(tmuxSession);
    if (screen === null) return;

    const action = sweepDialogs(screen);
    if (action) {
      await hygieneKeySender(tmuxSession, action.keys, action.interKeyDelayMs);
      log(`[inject] ${action.logMessage} for '${codeName}' before injection`);
      // Give the TUI a beat to drop the overlay before re-reading the pane.
      await new Promise((r) => setTimeout(r, 300));
      screen = paneCapture(tmuxSession) ?? '';
    }

    const orphan = extractInputBoxText(screen);
    if (orphan) {
      log(
        `[inject] clearing orphaned input for '${codeName}' before injection (input_hash=${simpleTextHash(orphan)}, len=${orphan.length})`,
      );
      await hygieneKeySender(tmuxSession, ['C-u'], 0);
    }
  } catch {
    // Hygiene is best-effort — never block the actual send on it.
  }
}

/**
 * ENG-5435: thin wrapper exposing the tmux send-keys path to the
 * manager-worker (which now also injects prompts via the hybrid
 * kanban-work mode). Routes through the same `armSender` binding so
 * the existing `__setArmSender` test seam still intercepts manager-side
 * calls. Returns true on success, false on any tmux/exec failure.
 */
export function sendToAgent(tmuxSession: string, command: string): boolean {
  return armSender(tmuxSession, command);
}

/**
 * ENG-5435: exported so the manager-worker hybrid path can gate
 * injects on a ready prompt. Same 10s cap and `❯` detection as the
 * spawn-time arm uses. Kept on `_internals` too for existing tests.
 */
export async function isAgentPromptReady(tmuxSession: string): Promise<boolean> {
  return waitForPromptReady(tmuxSession);
}

// Exported for unit testing — see __tests__/persistent-session-dialogs.test.ts.
export const _internals = {
  isLoginPickerVisible,
  isResumeModeDialogVisible,
  detectFailureSignature,
  // ENG-6039 test seams: seed/clear the module-private session map so
  // prepareForRespawn's rotation gate can be exercised without tmux.
  __seedSession(session: PersistentSession): void {
    sessions.set(session.codeName, session);
  },
  __clearSessions(): void {
    sessions.clear();
  },
  isClaudeProcessAliveInTmux,
  waitForPromptReady,
  // ENG-5770: exported so the unit test in claude-model-alias.test.ts can
  // exercise the send/skip decision without spawning a real tmux session.
  maybeSendFastMode,
  // Test seam: swap the tmux send-keys path so tests don't have to
  // spawn a real tmux server. ESM module-binding makes
  // `vi.spyOn(module, 'execFileSync')` ineffective for in-module
  // callers, so we route through this shim.
  __setArmSender(fn: ArmSender | null): void {
    armSender = fn ?? defaultArmSender;
  },
  // ENG-6017 test seam: swap the pane capture used by the inject-time
  // hygiene so unit tests can simulate dialog overlays / orphaned input
  // without a tmux server. null restores the real capture.
  __setPaneCapture(fn: PaneCapture | null): void {
    paneCapture = fn ?? defaultPaneCapture;
  },
  // ENG-6017 test seam: swap the hygiene key sender (dialog dismissal +
  // C-u clear) so unit tests can assert keystrokes without tmux.
  __setHygieneKeySender(fn: HygieneKeySender | null): void {
    hygieneKeySender = fn ?? defaultHygieneKeySender;
  },
  // Test-only resets so each test starts from a clean slate.
  __resetZombieState(): void {
    zombieProbeCache.clear();
    pendingZombieDetections.clear();
  },
  __getSessionsMap(): Map<string, PersistentSession> {
    return sessions;
  },
  __peekPendingZombie(codeName: string): ZombieDetectionRecord | null {
    return pendingZombieDetections.get(codeName) ?? null;
  },
};

// ---------------------------------------------------------------------------
// Task injection (tmux send-keys)
// ---------------------------------------------------------------------------

// ENG-5599 / ENG-6345: richer inject outcome so callers can tell "sent but
// unverified" (tmux send-keys landed) apart from "not delivered at all". The
// scheduled-task route needs this: a bare `false` (which send-keys returns even
// after sending the keys) would make it both nudge the live session AND spawn
// the legacy claude -p oneshot — double-executing the task. In-session delivery
// is tmux send-keys only (acpx removed, ENG-6345), so `delivered` is now
// structurally always false; `fallbackUsed` = the keystroke landed (submission
// unverified); neither = genuine failure.
export interface InjectResult {
  delivered: boolean;
  fallbackUsed: boolean;
}

// Boolean-returning wrapper preserved for the many existing callers. With acpx
// removed (ENG-6345) `delivered` is always false, so this wrapper now always
// returns false — exactly the path those callers already took, since the acpx
// exec branch never ran on the tmux-spawned fleet.
export async function injectMessage(
  codeName: string,
  type: 'task' | 'chat' | 'system',
  content: string,
  meta?: Record<string, string>,
  log?: (msg: string) => void,
): Promise<boolean> {
  return (await injectMessageWithStatus(codeName, type, content, meta, log)).delivered;
}

export async function injectMessageWithStatus(
  codeName: string,
  type: 'task' | 'chat' | 'system',
  content: string,
  meta?: Record<string, string>,
  log?: (msg: string) => void,
): Promise<InjectResult> {
  const _log = log ?? ((_: string) => {});
  const session = sessions.get(codeName);
  if (!session || session.status !== 'running') {
    _log(`[inject] SKIP '${codeName}' — session ${session ? `status=${session.status}` : 'not found in Map'}`);
    return { delivered: false, fallbackUsed: false };
  }

  const prefix = meta?.task_name ? `[Task: ${meta.task_name}] ` : '';
  const text = prefix + content;

  // In-session delivery is tmux send-keys via the shared armSender seam
  // (testable, and submits with a trailing Enter — see defaultArmSender).
  //
  // ENG-5782: collapse newlines before handing to tmux. Multi-line content
  // triggers tmux's bracketed-paste wrapping when passed to send-keys; once
  // Claude Code's TUI sees the paste-start escape, the trailing `Enter`
  // argument is captured as a literal newline *within* the paste rather
  // than as a "submit current input" event. The result: the prompt lands
  // in the input buffer perfectly readable but is never sent. So callers can
  // keep building multi-line content (run-boundary markers on their own line,
  // structured nudges, etc.) and we adapt it here. Inert markers like
  // <!-- agt-run:UUID --> remain parseable on a single line — see the
  // RUN_MARKER_RE regex which doesn't care about line position.
  const singleLineText = text.replace(/\s*\n+\s*/g, ' ').trim();
  // ENG-6017: dismiss any dialog overlaying the REPL and clear orphaned
  // input-box text before typing, so the send can't be eaten by a dialog
  // or concatenate onto a previously-stuck message.
  await preSendPaneHygiene(`agt-${codeName}`, codeName, _log);
  const sent = sendToAgent(`agt-${codeName}`, singleLineText);
  if (sent) {
    // tmux send-keys doesn't guarantee submission, so it's not a *confirmed*
    // delivery (delivered:false) — but the keystroke did land, so fallbackUsed
    // is true. injectMessage() still collapses this to `false` for legacy
    // callers; callers that must not double-act (the scheduled-task route)
    // read fallbackUsed and treat it as "reached the session, don't also spawn
    // the oneshot".
    _log(`[inject] tmux send-keys sent for '${codeName}' — unverified (delivered=false, fallbackUsed=true)`);
    return { delivered: false, fallbackUsed: true };
  }
  _log(`[inject] tmux send-keys failed for '${codeName}'`);
  return { delivered: false, fallbackUsed: false };
}

// ---------------------------------------------------------------------------
// Session management
// ---------------------------------------------------------------------------

export function stopPersistentSession(codeName: string, log: (msg: string) => void): void {
  const session = sessions.get(codeName);
  if (!session) return;

  log(`[persistent-session] Stopping session for '${codeName}'`);
  session.status = 'stopped';

  try {
    // ENG-6174: capture stderr (was 2>/dev/null + stdio:'ignore') so a kill
    // that DIDN'T take effect can't masquerade as a clean stop. tmux exits
    // non-zero with "can't find session" / "no server running" when the target
    // is already gone — the benign, expected case on most stops. Anything else
    // (tmux missing, permission, unexpected state) means the session we believe
    // we stopped may still be alive; surface it instead of swallowing, since a
    // half-completed stop is exactly the shape behind the ENG-6174 stuck restart.
    // execFileSync (arg array, no shell) — codeName is kebab-case-validated, but
    // defense-in-depth + consistency with the rest of this file (CodeRabbit).
    execFileSync('tmux', ['kill-session', '-t', `agt-${codeName}`], { stdio: ['ignore', 'ignore', 'pipe'] });
  } catch (err) {
    const stderr = ((err as { stderr?: Buffer | string }).stderr ?? '').toString().trim();
    const alreadyGone = /can't find session|no server running/i.test(stderr);
    if (!alreadyGone) {
      log(
        `[persistent-session] WARN tmux kill-session for '${codeName}' failed unexpectedly: ` +
        `${stderr || (err as Error).message} — the session may still be running (ENG-6174)`,
      );
    }
  }

  sessions.delete(codeName);

  // ENG-4808: claude exiting should take its child channel-MCP processes
  // (telegram-channel.js, slack-channel.js, direct-chat-channel.js) with
  // it, but in practice those children survive the parent — node's stdio
  // close-on-parent-exit isn't always honoured, especially when claude is
  // killed via tmux SIGHUP. Without an explicit reap, every restart leaks
  // a tree of long-pollers each holding the agent's bot token (observed
  // 6+ orphans on agt-aws-1 during the Vigil debugging session). Schedule
  // the reap after a short delay so claude has a chance to clean up its
  // own children — anything still alive after that is fair game.
  setTimeout(() => {
    reapOrphanChannelMcps({ log });
  }, 3_000).unref();
}

export function getSessionState(codeName: string): PersistentSession | null {
  return sessions.get(codeName) ?? null;
}

// ---------------------------------------------------------------------------
// Zombie detection (ENG-5391)
//
// Claude can die inside a live tmux pane — the shell prompt is left behind,
// `tmux has-session` keeps reporting the session as alive, and the manager's
// existing health check never trips. On alyve-host-1 (2026-05-21) this caused
// ~3-7 hours of silent unresponsiveness on `dwight` before the tmux session
// itself was eventually replaced.
//
// We close the gap by probing for the actual claude process inside each
// "healthy" tmux session. The probe matches on `--name <tmuxSession>` —
// the same flag the manager passes to claude at spawn (see line ~607),
// reused successfully by `hasMcpChildren()`. When the probe says no claude
// process is alive, we treat the session as crashed, kill the dead tmux
// shell so the next spawn isn't blocked by "duplicate session", and stash
// a record for manager-worker to ship as an audit_log event.
//
// Cost guard: `isSessionHealthy` is called many times per tick per agent.
// A short TTL cache (ZOMBIE_PROBE_TTL_MS) keeps the pgrep call at one per
// ~30s per agent — well inside the < 5 min detection target.
//
// Grace window: claude can take 10-30s to fully start. We don't probe
// within the first ZOMBIE_STARTUP_GRACE_MS after spawn / re-discovery,
// which avoids false positives during boot.
// ---------------------------------------------------------------------------

const ZOMBIE_PROBE_TTL_MS = 30_000;
const ZOMBIE_STARTUP_GRACE_MS = 60_000;

interface ZombieProbeCacheEntry {
  at: number;
  alive: boolean;
}
const zombieProbeCache = new Map<string, ZombieProbeCacheEntry>();

export interface ZombieDetectionRecord {
  codeName: string;
  tmuxSession: string;
  detectedAt: number;
  /** Last few lines of the pane log captured at detection time. */
  paneTail: string | null;
}
const pendingZombieDetections = new Map<string, ZombieDetectionRecord>();

// ENG-5832: the pgrep matching (ERE anchoring + the `--` option-terminator
// guard) moved to @augmented/core `runtime/session-probe.ts` so the channel
// servers reuse the exact same logic. The shared primitive is tri-state
// (alive | dead | unknown) to stay safe to reuse on hosts without pgrep; the
// manager only ever cared about the boolean "is claude alive", and treats
// both 'dead' and 'unknown' as crashed — exactly the pre-extraction behaviour
// (the old local copy returned false on any catch, ENOENT included).
function isClaudeProcessAliveInTmux(tmuxSession: string): boolean {
  // ENG-6670: under Docker isolation the host `pgrep -f '--name agt-<code>'`
  // matches the `docker run --name agt-<code>` WRAPPER argv even after the
  // IN-CONTAINER claude (PID 1) has died — masking the zombie so this never
  // reports a crash. Probe INSIDE the container instead: claude is PID 1, so if
  // it exits the container stops and `docker exec` fails (confident dead). A
  // transient docker error is treated as ALIVE (fail-safe) — a wrong 'dead'
  // here would trigger the very restart/flap we're trying to avoid, and the
  // Docker-aware presence-reaper (ENG-6660) is the backstop for a genuinely
  // dead in-container session.
  const codeName = tmuxSession.replace(/^agt-/, '');
  if (isolationMode(codeName) === 'docker') {
    try {
      const out = execFileSync('docker', ['exec', `agt-${codeName}`, 'pgrep', '-f', 'claude'], {
        encoding: 'utf-8',
        timeout: 8_000,
      }).trim();
      return out.length > 0;
    } catch (err) {
      // pgrep exit 1 (container running, no claude) or docker exit 125/126
      // (container gone / not running) = confident dead. ENOENT (no docker),
      // timeout, or any other status = can't tell -> fail-safe ALIVE.
      const e = err as NodeJS.ErrnoException & { status?: number | null };
      if (e?.status === 1 || e?.status === 125 || e?.status === 126) return false;
      return true;
    }
  }
  return probeClaudeProcessInTmux(tmuxSession) === 'alive';
}

/**
 * Pop the pending zombie-detection record for an agent, if any. Manager-
 * worker calls this in the unhealthy-session branch to emit an audit_log
 * event before respawning. Idempotent — returns null once consumed.
 */
export function takeZombieDetection(codeName: string): ZombieDetectionRecord | null {
  const record = pendingZombieDetections.get(codeName);
  if (record) pendingZombieDetections.delete(codeName);
  return record ?? null;
}

/**
 * Check if a persistent session is healthy.
 *
 * Two conditions both need to hold:
 *   1. The tmux session named `agt-<codeName>` exists (existing check).
 *   2. A claude process is actually running inside it (ENG-5391).
 *
 * Also detects sessions from previous manager runs (not in the Map).
 */
export function isSessionHealthy(codeName: string): boolean {
  const tmuxSession = `agt-${codeName}`;

  // Check if tmux session exists
  try {
    execSync(`tmux has-session -t ${tmuxSession} 2>/dev/null`, { stdio: 'ignore' });
  } catch {
    // tmux session doesn't exist — mark as crashed but don't increment
    // restartCount here (that happens in spawnSession on actual failure)
    const session = sessions.get(codeName);
    if (session && session.status === 'running') {
      session.status = 'crashed';
      // ENG-4659: capture the pane log tail BEFORE the next spawn
      // overwrites pane.log. Stash it on the session so the next
      // unhealthy log line + the prepareForRespawn recovery hook can
      // both read it. Also track consecutive failures with the same
      // UUID so we only rotate after >= 2 fails (one transient failure
      // doesn't lose today's history).
      session.lastFailureTail = readPaneLogTail(codeName);
      // The UUID just used to spawn (currentSessionId) vs the UUID that
      // last failed (lastFailureSessionId). When they match, we're
      // failing on the same UUID twice in a row — increment the gate.
      // When they differ (fresh spawn after rotation, first-ever
      // failure, etc.) reset to 1. Comparing lastFailureSessionId to
      // itself was the original CodeRabbit-caught bug.
      const failedUuid = session.currentSessionId;
      if (failedUuid && failedUuid === session.lastFailureSessionId) {
        session.consecutiveSameUuidFailures += 1;
      } else {
        session.consecutiveSameUuidFailures = 1;
      }
      session.lastFailureSessionId = failedUuid;
    }
    return false;
  }

  // tmux session exists — ensure it's tracked in the Map
  if (!sessions.has(codeName)) {
    // ENG-7328: this branch reattaches to a tmux session this manager process
    // doesn't track in its Map - i.e. a session that survived a manager restart.
    // Stamp its REAL creation time (tmux `#{session_created}`, epoch seconds),
    // not Date.now(): a Date.now() stamp here is strictly after any pending
    // restart_requested_at, so the restart-progress poll (which keys "back
    // online" on startedAt > restart_requested_at) would false-positive on a
    // stale surviving session that was never actually respawned. The true
    // creation time is older than the request, so it correctly does not trip it.
    let startedAt = Date.now();
    try {
      const created = execFileSync('tmux', ['display', '-p', '-t', tmuxSession, '#{session_created}'], {
        encoding: 'utf-8',
        timeout: 3000,
      }).trim();
      const secs = Number(created);
      if (Number.isFinite(secs) && secs > 0) startedAt = secs * 1000;
    } catch { /* tmux query failed - fall back to Date.now() (pre-ENG-7328 behaviour) */ }
    sessions.set(codeName, {
      codeName,
      startedAt,
      restartCount: 0,
      status: 'running',
      currentSessionId: null,
      lastFailureTail: null,
      lastFailureSessionId: null,
      consecutiveSameUuidFailures: 0,
      agentTimezone: null,
    });
  }

  const session = sessions.get(codeName)!;
  if (session.status !== 'running') {
    session.status = 'running';
  }

  // ENG-5391: tmux session exists — verify a claude process is actually
  // running inside it. Skip during the startup grace window so we don't
  // false-positive on a session that's still booting.
  const startedAt = session.startedAt;
  const withinGrace =
    startedAt != null && (Date.now() - startedAt) < ZOMBIE_STARTUP_GRACE_MS;

  if (!withinGrace) {
    const cached = zombieProbeCache.get(codeName);
    const cacheFresh = cached !== undefined && (Date.now() - cached.at) < ZOMBIE_PROBE_TTL_MS;
    const claudeAlive = cacheFresh ? cached.alive : isClaudeProcessAliveInTmux(tmuxSession);
    if (!cacheFresh) {
      zombieProbeCache.set(codeName, { at: Date.now(), alive: claudeAlive });
    }

    if (!claudeAlive) {
      // Zombie state: tmux session lingers but claude is gone. Capture
      // the pane tail BEFORE we kill the session so the next "unhealthy"
      // log line has the same forensics the regular crash path gets.
      const paneTail = readPaneLogTail(codeName);

      // Tear down the dead tmux session so the next spawnSession's
      // `tmux new-session` doesn't fail with "duplicate session" and
      // wedge the agent in a permanent zombie.
      try {
        execFileSync('tmux', ['kill-session', '-t', tmuxSession], { stdio: 'ignore' });
      } catch {
        // Race: another caller (or `stopPersistentSession`) already killed
        // it. The end state we want is unchanged either way.
      }

      session.status = 'crashed';
      session.lastFailureTail = paneTail;
      // Same consecutive-failure bookkeeping the tmux-gone path does so
      // the rotation gate behaves consistently across both crash modes.
      const failedUuid = session.currentSessionId;
      if (failedUuid && failedUuid === session.lastFailureSessionId) {
        session.consecutiveSameUuidFailures += 1;
      } else {
        session.consecutiveSameUuidFailures = 1;
      }
      session.lastFailureSessionId = failedUuid;

      // Stash for manager-worker to forward as an audit_log event. Only
      // record on transition (don't overwrite an unconsumed record) so a
      // hot loop of unhealthy checks doesn't emit duplicate audit rows.
      if (!pendingZombieDetections.has(codeName)) {
        pendingZombieDetections.set(codeName, {
          codeName,
          tmuxSession,
          detectedAt: Date.now(),
          // Cap pane tail to keep the audit payload bounded.
          paneTail: paneTail ? paneTail.slice(-1000) : null,
        });
      }
      zombieProbeCache.delete(codeName);
      return false;
    }
  }

  return true;
}

export function resetRestartCount(codeName: string): void {
  const session = sessions.get(codeName);
  if (session) session.restartCount = 0;
}

// ---------------------------------------------------------------------------
// Diagnostics — collect session health info for remote debugging
// ---------------------------------------------------------------------------

export interface SessionDiagnostics {
  codeName: string;
  status: 'running' | 'starting' | 'stopped' | 'crashed' | 'unknown';
  startedAt: string | null;
  restartCount: number;
  tmuxAlive: boolean;
  screenCapture: string | null; // last N lines from tmux pane
  launchArgs: string | null; // process args
  channelStatus: string | null; // extracted from screen capture
  // ENG-7328: whether this agent runs Docker-isolated (ADR-0014). The restart-
  // progress UI watches a respawn longer (6 min vs 3) for isolated agents
  // because the container + egress/squid teardown-and-recreate legitimately
  // runs slower than a bare-claude restart. Resolved per-agent from
  // isolationMode(codeName) so AGT_ISOLATION_AGENTS phased rollouts are honoured.
  isolated: boolean;
}

export function collectDiagnostics(codeNames: string[]): SessionDiagnostics[] {
  return codeNames.map((codeName) => {
    const session = sessions.get(codeName);
    const tmuxSession = `agt-${codeName}`;
    let tmuxAlive = false;
    let screenCapture: string | null = null;
    let launchArgs: string | null = null;
    let channelStatus: string | null = null;

    // Check tmux session (execFileSync to avoid shell injection)
    try {
      execFileSync('tmux', ['has-session', '-t', tmuxSession], { stdio: 'ignore' });
      tmuxAlive = true;
    } catch { /* session doesn't exist */ }

    // Capture last 30 lines from tmux pane
    if (tmuxAlive) {
      try {
        screenCapture = execFileSync('tmux', ['capture-pane', '-t', tmuxSession, '-p', '-S', '-30'], {
          encoding: 'utf-8',
          timeout: 3000,
        }).trim();
      } catch { /* non-fatal */ }
    }

    // Get process args via ps (safe — no user input in command)
    try {
      const psOutput = execFileSync('ps', ['aux'], { encoding: 'utf-8', timeout: 3000 });
      const line = psOutput.split('\n').find((l) => l.includes(`agt-${codeName}`) && !l.includes('grep'));
      if (line) {
        const match = line.match(/claude\s+.*/);
        launchArgs = match ? match[0].slice(0, 500) : null;
      }
    } catch { /* non-fatal */ }

    // Extract channel status from screen capture.
    // Only check the last 5 lines for current state — startup errors
    // may linger in scroll history but the agent could be healthy now.
    if (screenCapture) {
      const recentLines = screenCapture.split('\n').slice(-5).join('\n');
      const isIdle = recentLines.includes('❯');

      if (isIdle) {
        // Agent is at prompt — channels are likely working
        // Check full capture for persistent errors only
        if (screenCapture.includes('Channels require claude.ai authentication')) {
          channelStatus = 'error: auth required';
        } else {
          channelStatus = 'ok';
        }
      } else if (recentLines.includes('CHANNEL_ERROR') || recentLines.includes('CLOSED')) {
        channelStatus = 'error: disconnected';
      } else if (recentLines.includes('no MCP server configured')) {
        channelStatus = 'error: MCP server not found';
      } else if (recentLines.includes('ignored')) {
        channelStatus = 'error: channels ignored';
      } else {
        channelStatus = 'ok';
      }
    }

    return {
      codeName,
      status: tmuxAlive
        ? (session?.status ?? 'running')
        : (session?.status === 'running' ? 'crashed' : session?.status ?? 'unknown'),
      startedAt: session?.startedAt ? new Date(session.startedAt).toISOString() : null,
      restartCount: session?.restartCount ?? 0,
      tmuxAlive,
      screenCapture: screenCapture ? screenCapture.slice(-2000) : null, // limit size
      launchArgs,
      channelStatus,
      isolated: isolationMode(codeName) === 'docker',
    };
  });
}

export function stopAllSessions(log: (msg: string) => void): void {
  for (const codeName of sessions.keys()) {
    stopPersistentSession(codeName, log);
  }
}

export async function stopAllSessionsAndWait(
  log: (msg: string) => void,
  opts: { timeoutMs: number },
): Promise<void> {
  const codeNames = [...sessions.keys()];
  if (codeNames.length === 0) return;

  for (const codeName of codeNames) {
    stopPersistentSession(codeName, log);
  }

  await new Promise<void>((resolve) => setTimeout(resolve, Math.min(opts.timeoutMs, 2000)));
}

export function getProjectDir(codeName: string): string {
  return join(homedir(), '.augmented', codeName, 'project');
}
